// ========================================
// 未来属性カードの固有効果
// ========================================

import {
  getPlayerContext,
  millDeck,
  millOpponentDeck,
  checkAttribute,
  conditionalDamage,
  searchCard,
  reviveFromGraveyard,
  destroyMonster,
  drawCards,
  healLife,
  modifyAttack,
  modifyHP,
} from '../effectHelpers';
import { fireLeaveFieldTrigger } from '../triggerEngine';
import { hasCategory } from '../../utils/helpers';
import { STATUS_EFFECT_TYPES, statusEffectEngine } from '../statusEffects';
import type { CardEffectRegistry } from './index';
import type { MonsterInstance, CardWithId, CardData, PlayerNumber } from '../../types';

/**
 * 未来属性カードの固有効果
 */
export const futureCardEffects: CardEffectRegistry = {
  /**
   * C0000279: 虚蝕の残響者
   * 【召喚時】相手のデッキ上1枚を墓地に送る
   * 基本技: 自分のデッキ上1枚を墓地に送る、それが「未来属性」カードなら相手プレイヤーに300ダメージ、違えばこのカードに300ダメージ
   */
  C0000279: (skillText, context) => {
    const { addLog } = context;

    // 召喚時効果
    if (skillText.includes('【召喚時】')) {
      millOpponentDeck(context, 1);
      return true;
    }

    // 基本技
    if (context.skillType === 'basic') {
      const milledCards = millDeck(context, 1);
      if (milledCards.length === 0) {
        return false;
      }

      const milledCard = milledCards[0];
      if (checkAttribute(milledCard, '未来')) {
        conditionalDamage(context, 300, 'opponent');
        addLog('「未来属性」カードだった！', 'info');
      } else {
        conditionalDamage(context, 300, 'self_monster');
        addLog('「未来属性」カードではなかった...', 'info');
      }
      return true;
    }

    return false;
  },

  /**
   * C0000280: 星灯の少女エクラリア
   * 【召喚時】自分のデッキから《エクラシアの時空炉》1枚を手札に加える
   * 【自分メインフェイズ時】1ターンに1度、自分のモンスターが場に召喚されたとき、自分のデッキ上1枚を見て、
   *   それが「未来属性」カードならレスト状態のSPトークン１つをアクティブにする。
   * 基本技: このカードを手札に戻し、手札からコスト4以下の「未来属性」モンスター1体を召喚。
   */
  C0000280: (skillText, context) => {
    const { addLog, monsterIndex, setPendingHandSelection, summoningSickness = false } = context;
    const { myHand, setMyHand, myField, setMyField, currentPlayer } = getPlayerContext(context);

    if (skillText.includes('【召喚時】')) {
      const foundCard = searchCard(context, (card) => {
        return !!card.name && card.name.includes('エクラシアの時空炉');
      });
      return foundCard !== null;
    }

    // 基本技: このカードを手札に戻し、手札からコスト4以下の未来属性モンスター1体を召喚
    if (context.skillType === 'basic') {
      const thisCard = myField[monsterIndex];
      if (!thisCard) {
        addLog('星灯の少女エクラリア: カードが見つかりません', 'info');
        return false;
      }

      // 手札にコスト4以下の未来属性モンスターがあるか確認
      const validCards = myHand.filter(card =>
        card.type === 'monster' &&
        checkAttribute(card, '未来') &&
        card.cost <= 4
      );

      if (validCards.length === 0) {
        addLog('星灯の少女エクラリア: 手札にコスト4以下の未来属性モンスターがいません', 'info');
        return false;
      }

      // 手札選択
      setPendingHandSelection({
        message: '召喚するコスト4以下の未来属性モンスターを選択',
        filter: (card: CardWithId) => card.type === 'monster' && checkAttribute(card, '未来') && card.cost <= 4,
        callback: (selectedCards: CardWithId[]) => {
          if (!selectedCards || selectedCards.length === 0) return;
          const selectedCard = selectedCards[0];
          // このカードを手札に戻す（MonsterInstance特有のプロパティを除外）
          const { currentHp, currentAttack, canAttack, owner, charges, statusEffects, usedSkillThisTurn, ...returnedCard } = thisCard;

          // フィールドからこのカードを除去し、選択したカードを召喚
          setMyField(prev => prev.map((slot, idx) => {
            if (idx === monsterIndex) {
              // 新しいモンスターを召喚
              return {
                ...selectedCard,
                uniqueId: `${selectedCard.id}_${Date.now()}`,
                currentHp: selectedCard.hp ?? 0,
                currentAttack: selectedCard.attack ?? 0,
                canAttack: !summoningSickness, // 召喚酔い設定に依存
                usedSkillThisTurn: false,
                owner: currentPlayer,
                charges: [],
                statusEffects: [],
              };
            }
            return slot;
          }));

          // 手札から選択カードを除去し、このカードを手札に追加
          setMyHand(prev => {
            const newHand = prev.filter(c => c.uniqueId !== selectedCard.uniqueId);
            return [...newHand, returnedCard];
          });

          addLog(`星灯の少女エクラリアを手札に戻し、${selectedCard.name}を召喚！`, 'info');
        },
        selectionOwner: context.currentPlayer,
      });

      return true;
    }

    return false;
  },

  /**
   * C0000085: 胡乱なドローン
   * 【召喚時】デッキの上から1枚を墓地に送る。そのカードが未来属性の場合、次のターン終了時までこのカードの攻撃力を600に変更
   */
  C0000085: (skillText, context) => {
    const { addLog, monsterIndex } = context;
    const { setMyField } = getPlayerContext(context);

    if (skillText.includes('【召喚時】')) {
      const milledCards = millDeck(context, 1);
      if (milledCards.length === 0) {
        return false;
      }

      const milledCard = milledCards[0];
      if (checkAttribute(milledCard, '未来')) {
        // 攻撃力を600に変更
        setMyField(prev => prev.map((m, idx) => {
          if (idx === monsterIndex && m) {
            addLog(`${m.name}の攻撃力が600に変更！`, 'info');
            return { ...m, attack: 600 };
          }
          return m;
        }));
      }
      return true;
    }

    return false;
  },

  /**
   * C0000090: 時脈の観測者
   * 基本技: 自分の手札1枚をデッキに戻しシャッフルする。その後、1枚ドローする
   */
  C0000090: (skillText, context) => {
    const { addLog } = context;
    const { myHand, setMyHand, setMyDeck } = getPlayerContext(context);

    if (context.skillType === 'basic') {
      if (myHand.length === 0) {
        addLog('手札がありません', 'info');
        return false;
      }

      // 手札の最初のカードをデッキに戻す（本来はプレイヤーが選択）
      const cardToReturn = myHand[0];
      setMyHand(prev => prev.slice(1));
      setMyDeck(prev => {
        // シャッフル（簡易実装）
        const newDeck = [...prev, cardToReturn];
        return newDeck.sort(() => Math.random() - 0.5);
      });
      addLog(`手札から「${cardToReturn.name}」をデッキに戻した`, 'info');

      // 1枚ドロー
      drawCards(context, 1);
      return true;
    }

    return false;
  },

  /**
   * C0000105: 魔導炉の残骸獣
   * 【召喚時】自分の墓地の未来属性カード1枚をデッキに戻す。その後、相手プレイヤーに300ダメージを与える
   * 基本技: このカードの攻撃力をターン終了時まで1200に変更
   */
  C0000105: (skillText, context) => {
    const { addLog, monsterIndex } = context;
    const { myGraveyard, setMyGraveyard, setMyDeck, setMyField } = getPlayerContext(context);

    if (skillText.includes('【召喚時】')) {
      // 墓地から未来属性カードを検索
      const futureCard = myGraveyard.find(card => checkAttribute(card, '未来'));

      if (futureCard) {
        // デッキに戻す
        setMyGraveyard(prev => prev.filter(c => c.uniqueId !== futureCard.uniqueId));
        setMyDeck(prev => [...prev, futureCard]);
        addLog(`墓地から「${futureCard.name}」をデッキに戻した`, 'info');
      } else {
        addLog('墓地に未来属性カードがありません', 'info');
      }

      // 相手に300ダメージ
      conditionalDamage(context, 300, 'opponent');
      return true;
    }

    // 基本技: 攻撃力を1200に変更
    if (context.skillType === 'basic') {
      setMyField(prev => prev.map((m, idx) => {
        if (idx === monsterIndex && m) {
          addLog(`${m.name}の攻撃力が1200に変更！`, 'info');
          return { ...m, currentAttack: 1200, attackModifiedUntilEndOfTurn: true };
        }
        return m;
      }));
      return true;
    }

    return false;
  },

  /**
   * C0000116: 時空旅人ソラリア
   * 【召喚時】1枚ドローする。その後、手札を1枚デッキの下に戻す
   */
  C0000116: (skillText, context) => {
    const { addLog } = context;
    const { myHand, setMyHand, setMyDeck } = getPlayerContext(context);

    if (skillText.includes('【召喚時】')) {
      // 1枚ドロー
      const drawnCards = drawCards(context, 1);
      if (drawnCards.length === 0) {
        return false;
      }

      if (myHand.length === 0) {
        addLog('手札がありません', 'info');
        return true; // ドローは成功したのでtrue
      }

      // 手札の最初のカードをデッキの下に戻す（本来はプレイヤーが選択）
      const cardToReturn = myHand[0];
      setMyHand(prev => prev.slice(1));
      setMyDeck(prev => [...prev, cardToReturn]);
      addLog(`手札から「${cardToReturn.name}」をデッキの下に戻した`, 'info');

      return true;
    }

    return false;
  },

  /**
   * C0000183: 殲滅の魔導ゴーレム
   * 【召喚時】相手フィールド全体に600ダメージを与える
   */
  C0000183: (skillText, context) => {
    const { addLog } = context;
    const { opponentField, setOpponentField } = getPlayerContext(context);

    if (skillText.includes('【召喚時】')) {
      const monsters = opponentField.filter(m => m !== null);
      if (monsters.length === 0) {
        addLog('相手フィールドにモンスターがいません', 'info');
        return true;
      }

      // 全モンスターに600ダメージ（ログを先に出力）
      const damageResults = opponentField.map(m => {
        if (m) {
          const newHp = Math.max(0, m.currentHp - 600);
          addLog(`${m.name}に600ダメージ（残りHP: ${newHp}）`, 'damage');
          return { monster: m, newHp };
        }
        return null;
      });

      setOpponentField(prev => prev.map((m, idx) => {
        if (m && damageResults[idx]) {
          return { ...m, currentHp: damageResults[idx].newHp };
        }
        return m;
      }));

      return true;
    }

    return false;
  },

  /**
   * C0000249: ミランの使い魔未来鴉
   * 【召喚時】デッキの上から3枚を見て、好きな順番で戻す
   * 基本技: このターン中、手札の《鴉》モンスターの召喚コストを1軽減。
   */
  C0000249: (skillText, context) => {
    const { addLog } = context;
    const { myDeck, myHand, setMyHand } = getPlayerContext(context);

    if (skillText.includes('【召喚時】')) {
      if (myDeck.length >= 3) {
        const topCards = myDeck.slice(0, 3);
        addLog(`デッキの上から3枚を確認: ${topCards.map(c => c.name).join(', ')}`, 'info');
        addLog('（順番はそのまま戻しました）', 'info');
      } else {
        addLog(`デッキの残りは${myDeck.length}枚です`, 'info');
      }

      // 実際の順番変更は UI が必要なので、現在は何もしない
      return true;
    }

    // 基本技: 手札の《鴉》モンスターの召喚コストを1軽減
    if (context.skillType === 'basic') {
      // 手札の《鴉》または【カラス】モンスターを探す
      const ravenCards = myHand.filter(card =>
        card.type === 'monster' &&
        ((card.name && (card.name.includes('鴉') || card.name.includes('カラス'))) ||
         (card.category && card.category.includes('【カラス】')))
      );

      if (ravenCards.length === 0) {
        addLog('ミランの使い魔未来鴉の基本技: 手札に《鴉》モンスターがいません', 'info');
        return false;
      }

      // 手札の《鴉》モンスターのコストを1軽減
      setMyHand(prev => prev.map(card => {
        if (card.type === 'monster' &&
            ((card.name && (card.name.includes('鴉') || card.name.includes('カラス'))) ||
             (card.category && card.category.includes('【カラス】')))) {
          return {
            ...card,
            cost: Math.max(0, card.cost - 1),
            costReduced: true,
            costReductionSource: 'ミランの使い魔未来鴉',
          };
        }
        return card;
      }));

      addLog(`ミランの使い魔未来鴉の基本技: 手札の《鴉》モンスター${ravenCards.length}体のコストを1軽減！`, 'info');
      return true;
    }

    return false;
  },

  /**
   * C0000250: 時空渡りのカモメ
   * 【召喚時】相手の手札を1枚ランダムに墓地に送る
   * 基本技：このカードの攻撃力をターン終了時まで800アップ。
   */
  C0000250: (skillText, context) => {
    const { addLog, monsterIndex } = context;
    const { opponentHand, setOpponentHand, setOpponentGraveyard, myField, setMyField } = getPlayerContext(context);

    if (skillText.includes('【召喚時】')) {
      if (opponentHand.length === 0) {
        addLog('相手の手札がありません', 'info');
        return true;
      }

      // ランダムに1枚選択
      const randomIndex = Math.floor(Math.random() * opponentHand.length);
      const discardedCard = opponentHand[randomIndex];

      setOpponentHand(prev => prev.filter((_, idx) => idx !== randomIndex));
      setOpponentGraveyard(prev => [...prev, discardedCard]);
      addLog(`相手の手札から「${discardedCard.name}」を墓地に送った`, 'damage');

      return true;
    }

    // 基本技：自身の攻撃力+800
    if (context.skillType === 'basic') {
      const monster = myField[monsterIndex];
      if (!monster) {
        addLog('時空渡りのカモメの基本技: 対象が見つかりません', 'info');
        return false;
      }

      setMyField(prev => prev.map((m, idx) => {
        if (idx === monsterIndex && m) {
          const newAtk = (m.currentAttack ?? m.attack ?? 0) + 800;
          addLog(`時空渡りのカモメの基本技: 攻撃力+800！（${m.currentAttack || m.attack} → ${newAtk}）`, 'info');
          return {
            ...m,
            currentAttack: newAtk,
            atkBuffedUntilEndOfTurn: true,
          };
        }
        return m;
      }));

      return true;
    }

    return false;
  },

  /**
   * C0000251: 紫鴉の魔導士ミラン
   * 【召喚時】デッキから未来属性魔法カード1枚を手札に加える
   * 基本技：自分のデッキの上から1枚を見て、デッキの上か下に置く。
   * 上級技：デッキから《時の》と名のついたコスト4の未来属性魔法カードを発動する。
   */
  C0000251: (skillText, context) => {
    const { addLog, setPendingDeckReview } = context;
    const { myDeck, setMyDeck, setMyHand, setMyGraveyard, myField } = getPlayerContext(context);

    if (skillText.includes('【召喚時】')) {
      const foundCard = searchCard(context, (card) => {
        return checkAttribute(card, '未来') && card.type === 'magic';
      });
      return foundCard !== null;
    }

    // 基本技：デッキトップ1枚を確認
    if (context.skillType === 'basic') {
      if (myDeck.length === 0) {
        addLog('紫鴉の魔導士ミランの基本技: デッキがありません', 'info');
        return false;
      }

      const topCard = myDeck[0];
      addLog(`紫鴉の魔導士ミランの基本技: デッキトップを確認「${topCard.name}」`, 'info');

      // 確認後、上か下に置く選択（簡易実装: そのまま上に戻す）
      // TODO: UIで「上に置く/下に置く」を選択
      addLog('（デッキの上にそのまま戻しました）', 'info');

      return true;
    }

    // 上級技：デッキから《時の》と名のついたコスト4の未来属性魔法カードを発動
    if (context.skillType === 'advanced') {
      // デッキから条件に合うカードを探す
      const targetIndex = myDeck.findIndex(card =>
        card.type === 'magic' &&
        checkAttribute(card, '未来') &&
        card.cost === 4 &&
        card.name && card.name.includes('時の')
      );

      if (targetIndex === -1) {
        addLog('紫鴉の魔導士ミランの上級技: デッキに《時の》コスト4の未来属性魔法がありません', 'info');
        return false;
      }

      const targetCard = myDeck[targetIndex];

      // デッキから削除
      setMyDeck(prev => prev.filter((_, idx) => idx !== targetIndex));

      // 魔法カードを発動（簡易実装: 墓地に送る＋効果発動）
      setMyGraveyard(prev => [...prev, targetCard]);
      addLog(`紫鴉の魔導士ミランの上級技: デッキから「${targetCard.name}」を発動！`, 'info');

      // 該当魔法カードの効果を発動
      // C0000254: 時の歪曲 - 凍結+ATK0、ミランいれば破壊+ATK半分ダメージ
      // C0000262: 時の遅延 - 800全体ダメージ+技/魔法封印、未来モンスターいれば1体バウンス
      // C0000264: 時の逆流 - 墓地3枚戻して2ドロー、LP1500回復、レストSPアクティブ化
      // ここでは効果を直接実行するのは複雑なため、発動のみ通知
      addLog(`※「${targetCard.name}」の効果を手動で確認してください`, 'info');

      return true;
    }

    return false;
  },

  /**
   * C0000252: 未来鴉の群れ
   * 【自壊時】デッキから「未来属性」モンスター1体を墓地に送り、1枚ドローする。
   * 基本技：デッキから「未来属性」カードを場の未来属性モンスターにチャージする。
   * ※自壊時トリガーはcardTriggers/futureCards.jsに実装済み
   */
  C0000252: (skillText, context) => {
    const { addLog, setPendingMonsterTarget, setPendingDeckReview } = context;
    const { myDeck, setMyDeck, myField, setMyField } = getPlayerContext(context);

    // 基本技：デッキから「未来属性」カードを場の未来属性モンスターにチャージ
    if (context.skillType === 'basic') {
      // 場の未来属性モンスターを取得
      const futureMonsters = myField
        .map((m, idx) => ({ monster: m, index: idx }))
        .filter(({ monster }) => monster && checkAttribute(monster, '未来'));

      if (futureMonsters.length === 0) {
        addLog('未来鴉の群れの基本技: 場に未来属性モンスターがいません', 'info');
        return false;
      }

      // デッキから未来属性カードを探す
      const futureCardIndex = myDeck.findIndex(card => checkAttribute(card, '未来'));
      if (futureCardIndex === -1) {
        addLog('未来鴉の群れの基本技: デッキに未来属性カードがありません', 'info');
        return false;
      }

      const chargeCard = myDeck[futureCardIndex];

      const applyCharge = (targetIndex: number) => {
        // デッキからカードを削除
        setMyDeck(prev => prev.filter((_, idx) => idx !== futureCardIndex));

        // 対象モンスターにチャージを付与
        setMyField(prev => prev.map((m, idx) => {
          if (idx === targetIndex && m) {
            const currentChargeCount = m.chargeCount ?? 0;
            const currentCharges = m.charges ?? [];
            addLog(`未来鴉の群れの基本技: 「${chargeCard.name}」を「${m.name}」にチャージ！（チャージ: ${currentChargeCount} → ${currentChargeCount + 1}）`, 'info');
            return {
              ...m,
              chargeCount: currentChargeCount + 1,
              charges: [...currentCharges, chargeCard],
            };
          }
          return m;
        }));
      };

      // 1体のみの場合は自動選択
      if (futureMonsters.length === 1) {
        applyCharge(futureMonsters[0].index);
        return true;
      }

      // 複数の場合は選択UI
      if (setPendingMonsterTarget) {
        setPendingMonsterTarget({
          message: 'チャージする未来属性モンスターを選択',
          targetPlayer: 'self',
          validTargets: futureMonsters.map(t => t.index),
          callback: (selectedIndex: number) => {
            applyCharge(selectedIndex);
          },
          selectionOwner: context.currentPlayer,
        });
        return true;
      }

      // フォールバック
      applyCharge(futureMonsters[0].index);
      return true;
    }

    return false;
  },

  /**
   * C0000256: 紫時空の幻鴉
   * 【召喚時】自分の墓地の「未来属性」カード1枚を手札に加える。
   * 基本技：相手モンスター1体に500ダメージを与える。
   * 上級技：このカードのHPを半減して分裂する。
   */
  C0000256: (skillText, context) => {
    const { addLog, monsterIndex, setPendingMonsterTarget, summoningSickness = false } = context;
    const { myGraveyard, setMyGraveyard, setMyHand, myField, setMyField, opponentField, setOpponentField, setOpponentGraveyard, currentPlayer } = getPlayerContext(context);

    // 召喚時効果
    if (skillText.includes('【召喚時】')) {
      // 墓地から未来属性カードを検索
      const futureCard = myGraveyard.find(card => checkAttribute(card, '未来'));

      if (futureCard) {
        setMyGraveyard(prev => prev.filter(c => c.uniqueId !== futureCard.uniqueId));
        setMyHand(prev => [...prev, futureCard]);
        addLog(`墓地から「${futureCard.name}」を手札に加えた`, 'info');
        return true;
      } else {
        addLog('墓地に未来属性カードがありません', 'info');
        return false;
      }
    }

    // 基本技：相手モンスター1体に500ダメージ
    if (context.skillType === 'basic') {
      const validTargets = opponentField
        .map((m, i) => ({ monster: m, index: i }))
        .filter(({ monster }) => monster !== null);

      if (validTargets.length === 0) {
        addLog('紫時空の幻鴉: 相手フィールドにモンスターがいません', 'info');
        return false;
      }

      setPendingMonsterTarget({
        message: '500ダメージを与える相手モンスターを選択',
        validTargets: validTargets.map(t => t.index),
        targetPlayer: 'opponent',
        callback: (targetIndex: number) => {
          setOpponentField(prev => {
            const newField = [...prev];
            const target = newField[targetIndex];
            if (!target) return newField;

            const newHp = (target.currentHp ?? target.hp ?? 0) - 500;
            if (newHp <= 0) {
              // 破壊処理
              addLog(`紫時空の幻鴉の基本技: ${target.name}に500ダメージ！破壊した！`, 'damage');
              newField[targetIndex] = null;
              // 墓地送りは別途処理が必要
              setOpponentGraveyard(prev => [...prev, target]);
            } else {
              newField[targetIndex] = {
                ...target,
                currentHp: newHp,
              };
              addLog(`紫時空の幻鴉の基本技: ${target.name}に500ダメージ！（残りHP: ${newHp}）`, 'damage');
            }
            return newField;
          });
        },
        selectionOwner: context.currentPlayer,
      });
      return true;
    }

    // 上級技：このカードのHPを半減して分裂する
    if (context.skillType === 'advanced') {
      const monster = myField[monsterIndex];
      if (!monster) {
        addLog('紫時空の幻鴉: モンスターが存在しません', 'info');
        return false;
      }

      // 空きスロットを確認
      const emptySlotIndex = myField.findIndex((slot, i) => slot === null && i !== monsterIndex);
      if (emptySlotIndex === -1) {
        addLog('紫時空の幻鴉の上級技: 場が満杯で分裂できません', 'info');
        return false;
      }

      const currentHp = monster.currentHp ?? monster.hp ?? 0;
      const halfHp = Math.floor(currentHp / 2);

      if (halfHp <= 0) {
        addLog('紫時空の幻鴉の上級技: HPが足りず分裂できません', 'info');
        return false;
      }

      // 自身のHPを半減して、分裂体を召喚
      setMyField(prev => {
        const newField = [...prev];
        // 元のカードのHP半減
        newField[monsterIndex] = {
          ...monster,
          currentHp: halfHp,
        };
        // 分裂体を空きスロットに召喚（HP半減状態）
        const newSlotIndex = newField.findIndex((slot, i) => slot === null && i !== monsterIndex);
        if (newSlotIndex !== -1) {
          newField[newSlotIndex] = {
            ...monster,
            uniqueId: `${monster.id}_split_${Date.now()}`,
            currentHp: halfHp,
            maxHp: monster.hp ?? 0,
            currentAttack: monster.attack ?? 0,
            canAttack: !summoningSickness, // 召喚酔い設定に依存
            charges: [],
            statusEffects: [],
            usedSkillThisTurn: false,
            owner: currentPlayer,
          };
        }
        return newField;
      });

      addLog(`紫時空の幻鴉の上級技: HPを半減して分裂！（HP: ${halfHp}）`, 'info');
      return true;
    }

    return false;
  },

  /**
   * C0000267: 星翼の飛舟エクラオン
   * 基本技: デッキからコスト4以下の「未来属性」モンスター1体を召喚。このカードをデッキの下に戻す。
   */
  C0000267: (skillText, context) => {
    const { addLog, monsterIndex, summoningSickness = false } = context;
    const { myDeck, setMyDeck, myField, setMyField, currentPlayer } = getPlayerContext(context);

    if (context.skillType === 'basic') {
      // デッキからコスト4以下の未来属性モンスターを検索
      const targetIndex = myDeck.findIndex(card =>
        card.type === 'monster' &&
        checkAttribute(card, '未来') &&
        card.cost <= 4
      );

      if (targetIndex === -1) {
        addLog('デッキにコスト4以下の未来属性モンスターがいません', 'info');
        return false;
      }

      // 空きスロットを探す
      const emptySlotIndex = myField.findIndex(slot => slot === null);
      if (emptySlotIndex === -1) {
        addLog('フィールドに空きがありません', 'info');
        return false;
      }

      // デッキからカードを取得
      const targetCard = myDeck[targetIndex];
      const newDeck = myDeck.filter((_, idx) => idx !== targetIndex);

      // このカード（エクラオン）を取得
      const thisCard = myField[monsterIndex];
      if (!thisCard) {
        addLog('エラー: カードが見つかりません', 'info');
        return false;
      }

      // モンスターインスタンスを作成
      const monsterInstance = {
        ...targetCard,
        uniqueId: `${targetCard.id}_${Date.now()}`,
        currentHp: targetCard.hp ?? 0,
        currentAttack: targetCard.attack ?? 0,
        canAttack: !summoningSickness, // 召喚酔い設定に依存
        usedSkillThisTurn: false,
        owner: currentPlayer,
        charges: [],
        statusEffects: [],
      };

      // フィールドを更新：召喚＆このカードを除去
      setMyField(prev => prev.map((slot, idx) => {
        if (idx === emptySlotIndex) {
          return monsterInstance;
        }
        if (idx === monsterIndex) {
          return null; // このカードをフィールドから除去
        }
        return slot;
      }));

      // デッキを更新：このカードをデッキの下に追加
      setMyDeck([...newDeck, thisCard]);

      addLog(`デッキから「${targetCard.name}」を召喚！`, 'info');
      addLog(`「${thisCard.name}」をデッキの下に戻した`, 'info');

      // 「場を離れる時」トリガーを発動（デッキに戻る場合も発動）
      fireLeaveFieldTrigger(thisCard, context, 'return_to_deck');

      return true;
    }

    return false;
  },

  /**
   * C0000274: 星辰の魔導術師
   * 基本技: 場に《エクラリア》モンスターがいる場合、自分のデッキからコスト4以下「未来属性」魔法カード1枚を手札に加える。
   */
  C0000274: (skillText, context) => {
    const { addLog } = context;
    const { myField, myDeck, setMyDeck, setMyHand } = getPlayerContext(context);

    if (context.skillType === 'basic') {
      // 場に「エクラリア」モンスターがいるか確認
      const hasEclaria = myField.some((monster, slotIndex) => {
        if (monster !== null && monster !== undefined && !monster.name) {
          console.warn('[future.js C0000274] フィールドにnameがないモンスターがいます:', {
            slotIndex,
            monsterKeys: Object.keys(monster),
            monster,
            sourceCard: context.card?.name,
          });
          return false;
        }
        return monster !== null && monster !== undefined && monster.name &&
          (monster.name.includes('エクラリア') || hasCategory(monster, '【エクラリア】'));
      });

      if (!hasEclaria) {
        addLog('場に《エクラリア》モンスターがいません', 'info');
        return false;
      }

      // デッキからコスト4以下の未来属性魔法カードを検索
      const targetIndex = myDeck.findIndex(card =>
        card.type === 'magic' &&
        checkAttribute(card, '未来') &&
        card.cost <= 4
      );

      if (targetIndex === -1) {
        addLog('デッキにコスト4以下の未来属性魔法カードがありません', 'info');
        return false;
      }

      // カードを手札に加える
      const targetCard = myDeck[targetIndex];
      setMyDeck(prev => prev.filter((_, idx) => idx !== targetIndex));
      setMyHand(prev => [...prev, targetCard]);

      addLog(`デッキから「${targetCard.name}」を手札に加えた`, 'info');
      return true;
    }

    return false;
  },

  /**
   * C0000278: 灯守の少女
   * トリガーのみ（futureCards.jsで実装）
   * - 【召喚時】自分のデッキ上1枚を見て、それが「未来属性」カードなら手札に、違えばデッキ下に
   * - 【場を離れる時】自分の墓地の「未来属性」カード1枚をデッキに戻す
   */
  C0000278: (skillText, context) => {
    // このカードは基本技/上級技を持たない（トリガーのみ）
    return false;
  },

  /**
   * C0000268: 虚蝕の魔導兵
   * 召喚時効果はトリガー（futureCards.jsで実装）
   * 基本技: 自分の墓地の「未来属性」カード1枚をデッキに戻し、このカードの攻撃力をターン終了時まで800アップ。
   */
  C0000268: (skillText, context) => {
    const { addLog, monsterIndex } = context;
    const { myGraveyard, setMyGraveyard, setMyDeck, myField, setMyField } = getPlayerContext(context);

    // 基本技
    if (context.skillType === 'basic') {
      // 墓地から未来属性カードを検索
      const futureCard = myGraveyard.find(card => checkAttribute(card, '未来'));

      if (!futureCard) {
        addLog('墓地に未来属性カードがありません', 'info');
        return false;
      }

      // 墓地からデッキに戻す
      setMyGraveyard(prev => prev.filter(c => c.uniqueId !== futureCard.uniqueId));
      setMyDeck(prev => [...prev, futureCard]);
      addLog(`墓地から「${futureCard.name}」をデッキに戻した`, 'info');

      // このカードの攻撃力を800アップ（ターン終了時まで）
      const thisCard = myField[monsterIndex];
      if (thisCard) {
        setMyField(prev => prev.map((m, idx) => {
          if (idx === monsterIndex && m) {
            const newAttack = (m.currentAttack ?? m.attack ?? 0) + 800;
            addLog(`${m.name}の攻撃力が800アップ（${newAttack}）！`, 'info');
            return {
              ...m,
              currentAttack: newAttack,
              attackBuffUntilEndOfTurn: (m.attackBuffUntilEndOfTurn || 0) + 800,
            };
          }
          return m;
        }));
      }

      return true;
    }

    return false;
  },

  /**
   * C0000106: 次元渡り・ヴォイドウォーカー
   * エンドフェイズトリガーはfutureCards.jsで実装
   * 基本技: このカードの攻撃力をターン終了時まで1800に変更。
   */
  C0000106: (skillText, context) => {
    const { addLog, monsterIndex } = context;
    const { myField, setMyField } = getPlayerContext(context);

    // 基本技
    if (context.skillType === 'basic') {
      const thisCard = myField[monsterIndex];
      if (!thisCard) {
        return false;
      }

      const currentAttack = thisCard.currentAttack ?? thisCard.attack ?? 0;
      const targetAttack = 1800;

      // 既に1800以上なら効果なし
      if (currentAttack >= targetAttack) {
        addLog(`${thisCard.name}の攻撃力は既に${currentAttack}です`, 'info');
        return true;
      }

      // 攻撃力を1800に変更（ターン終了時まで）
      const buffAmount = targetAttack - currentAttack;
      setMyField(prev => prev.map((m, idx) => {
        if (idx === monsterIndex && m) {
          addLog(`${m.name}の攻撃力が${targetAttack}に変更！`, 'info');
          return {
            ...m,
            currentAttack: targetAttack,
            attackBuffUntilEndOfTurn: (m.attackBuffUntilEndOfTurn || 0) + buffAmount,
          };
        }
        return m;
      }));

      return true;
    }

    return false;
  },

  /**
   * C0000269: 永遠の灯の残響
   * 自分の墓地のコスト4以下「未来属性」モンスター1体を場に召喚。そのモンスターはターン終了時まで破壊されない。
   */
  C0000269: (skillText, context) => {
    const { addLog } = context;

    // 魔法カード効果
    const result = reviveFromGraveyard(
      context,
      (card) => checkAttribute(card, '未来') && card.cost <= 4,
      { indestructible: true }
    );

    if (result) {
      addLog('永遠の灯の残響の効果発動！', 'info');
    }
    return result;
  },

  /**
   * C0000277: 星屑の時導術
   * 【刹那詠唱】自分のデッキ上3枚を見て、1枚を手札に加え、残りを好きな順番で戻す。
   */
  C0000277: (skillText, context) => {
    const { addLog, setPendingDeckReview } = context;
    const { myDeck, setMyDeck, setMyHand } = getPlayerContext(context);

    if (myDeck.length === 0) {
      addLog('デッキにカードがありません', 'info');
      return true; // 効果処理は完了（対象なし）
    }

    const cardCount = Math.min(3, myDeck.length);
    const topCards = myDeck.slice(0, cardCount);
    addLog(`星屑の時導術: デッキ上${cardCount}枚を確認`, 'info');

    if (cardCount === 1) {
      // 1枚しかない場合、その1枚を手札に加える
      setMyHand((prev) => [...prev, topCards[0]]);
      setMyDeck((prev) => prev.slice(1));
      addLog(`${topCards[0].name}を手札に加えた`, 'info');
      return true;
    }

    // 2枚以上ある場合、プレイヤーに選択させる
    setPendingDeckReview({
      cards: topCards,
      title: '星屑の時導術',
      message: '手札に加えるカードを1枚選択してください（残りは順番を変更してデッキに戻します）',
      allowReorder: false,
      selectMode: { enabled: true, count: 1 },
      onSelect: (selectedCards: CardWithId[], remainingCards: CardWithId[]) => {
        const cardToHand = selectedCards[0];
        setMyHand((prev) => [...prev, cardToHand]);
        addLog(`${cardToHand.name}を手札に加えた`, 'info');

        if (remainingCards.length > 1) {
          // 残りカードの順番を選択させる
          setPendingDeckReview({
            cards: remainingCards,
            title: '星屑の時導術',
            message: 'デッキに戻すカードの順番を決めてください（上から順）',
            allowReorder: true,
            onConfirm: (reorderedCards: CardWithId[]) => {
              setMyDeck((prev) => [...reorderedCards, ...prev.slice(cardCount)]);
              addLog(`残りをデッキに戻した: ${reorderedCards.map((c) => c.name).join(' → ')}`, 'info');
            },
          });
        } else if (remainingCards.length === 1) {
          // 残りが1枚なら自動で戻す
          setMyDeck((prev) => [remainingCards[0], ...prev.slice(cardCount)]);
          addLog(`${remainingCards[0].name}をデッキに戻した`, 'info');
        }
      },
      selectionOwner: context.currentPlayer,
    });

    return true;
  },

  /**
   * C0000255: 未来の鴉予言
   * 自分のデッキの上から5枚を見て、1枚を手札に加え、残りを好きな順番で戻す。
   */
  C0000255: (skillText, context) => {
    const { addLog, setPendingDeckReview } = context;
    const { myDeck, setMyDeck, setMyHand } = getPlayerContext(context);

    if (myDeck.length === 0) {
      addLog('デッキにカードがありません', 'info');
      return true; // 効果処理は完了（対象なし）
    }

    const cardCount = Math.min(5, myDeck.length);
    const topCards = myDeck.slice(0, cardCount);
    addLog(`未来の鴉予言: デッキ上${cardCount}枚を確認`, 'info');

    if (cardCount === 1) {
      // 1枚しかない場合、その1枚を手札に加える
      setMyHand((prev) => [...prev, topCards[0]]);
      setMyDeck((prev) => prev.slice(1));
      addLog(`${topCards[0].name}を手札に加えた`, 'info');
      return true;
    }

    // 2枚以上ある場合、プレイヤーに選択させる
    setPendingDeckReview({
      cards: topCards,
      title: '未来の鴉予言',
      message: '手札に加えるカードを1枚選択してください（残りは順番を変更してデッキに戻します）',
      allowReorder: false,
      selectMode: { enabled: true, count: 1 },
      onSelect: (selectedCards: CardWithId[], remainingCards: CardWithId[]) => {
        const cardToHand = selectedCards[0];
        setMyHand((prev) => [...prev, cardToHand]);
        addLog(`${cardToHand.name}を手札に加えた`, 'info');

        if (remainingCards.length > 1) {
          // 残りカードの順番を選択させる
          setPendingDeckReview({
            cards: remainingCards,
            title: '未来の鴉予言',
            message: 'デッキに戻すカードの順番を決めてください（上から順）',
            allowReorder: true,
            onConfirm: (reorderedCards: CardWithId[]) => {
              setMyDeck((prev) => [...reorderedCards, ...prev.slice(cardCount)]);
              addLog(`残りをデッキに戻した`, 'info');
            },
          });
        } else if (remainingCards.length === 1) {
          // 残りが1枚なら自動で戻す
          setMyDeck((prev) => [remainingCards[0], ...prev.slice(cardCount)]);
          addLog(`${remainingCards[0].name}をデッキに戻した`, 'info');
        }
      },
      selectionOwner: context.currentPlayer,
    });

    return true;
  },

  /**
   * C0000271: 黄金の時姫エクラリア
   * 【召喚時】自分のデッキ上5枚を見て、コスト6以下のモンスター1枚を場に召喚、残りを好きな順番で戻す。
   * 【常時】自分の「未来属性」モンスターが与えるダメージを500アップ。（continuousEffectsで実装）
   * 基本技: 自分の墓地の「未来属性」カード1枚をデッキに戻し、レスト状態のSPトークン１つをアクティブにする（ターンに1度）。
   * 上級技: 勝負中1度のみ、自分のデッキ上3枚を公開し、1枚を場に召喚（コスト無視）。そのターン終了時、このカード以外の自分のモンスターをすべて墓地に送る。
   */
  C0000271: (skillText, context) => {
    const { addLog, monsterIndex, setPendingDeckReview, summoningSickness = false } = context;
    const {
      myDeck, setMyDeck, myField, setMyField, myGraveyard, setMyGraveyard,
      myRestedSP, setMyRestedSP, myActiveSP, setMyActiveSP, currentPlayer,
    } = getPlayerContext(context);

    // 召喚時効果（デッキ上5枚からコスト6以下のモンスター1体を召喚）
    if (skillText.includes('【召喚時】')) {
      if (myDeck.length === 0) {
        addLog('デッキにカードがありません', 'info');
        return true;
      }

      const cardCount = Math.min(5, myDeck.length);
      const topCards = myDeck.slice(0, cardCount);
      const eligibleMonsters = topCards.filter(card =>
        card.type === 'monster' && card.cost <= 6
      );

      addLog(`黄金の時姫エクラリア: デッキ上${cardCount}枚を確認`, 'info');

      if (eligibleMonsters.length === 0) {
        addLog('コスト6以下のモンスターがありませんでした', 'info');
        // 残りを戻す処理（順番選択）
        if (topCards.length > 1) {
          setPendingDeckReview({
            cards: topCards,
            title: '黄金の時姫エクラリア',
            message: 'デッキに戻すカードの順番を決めてください（上から順）',
            allowReorder: true,
            onConfirm: (reorderedCards: CardWithId[]) => {
              setMyDeck((prev) => [...reorderedCards, ...prev.slice(cardCount)]);
              addLog(`カードをデッキに戻した`, 'info');
            },
            selectionOwner: context.currentPlayer,
          });
        }
        return true;
      }

      // 空きスロットを確認
      const emptySlotIndex = myField.findIndex(slot => slot === null);
      if (emptySlotIndex === -1) {
        addLog('フィールドに空きがありません', 'info');
        return true;
      }

      // 対象モンスターを選択させる
      setPendingDeckReview({
        cards: topCards,
        title: '黄金の時姫エクラリア',
        message: '場に召喚するコスト6以下のモンスターを1体選択してください',
        allowReorder: false,
        selectMode: {
          enabled: true,
          count: 1,
          filter: (card: CardWithId) => card.type === 'monster' && card.cost <= 6,
        },
        onSelect: (selectedCards: CardWithId[], remainingCards: CardWithId[]) => {
          const cardToSummon = selectedCards[0];

          // モンスターインスタンスを作成
          const monsterInstance = {
            ...cardToSummon,
            uniqueId: `${cardToSummon.id}_${Date.now()}`,
            currentHp: cardToSummon.hp ?? 0,
            currentAttack: cardToSummon.attack ?? 0,
            canAttack: !summoningSickness, // 召喚酔い設定に依存
            usedSkillThisTurn: false,
            owner: currentPlayer,
            charges: [],
            statusEffects: [],
          };

          // フィールドに召喚
          setMyField(prev => prev.map((slot, idx) =>
            idx === emptySlotIndex ? monsterInstance : slot
          ));
          addLog(`${cardToSummon.name}を場に召喚！`, 'info');

          // 残りカードを戻す
          if (remainingCards.length > 1) {
            setPendingDeckReview({
              cards: remainingCards,
              title: '黄金の時姫エクラリア',
              message: 'デッキに戻すカードの順番を決めてください（上から順）',
              allowReorder: true,
              onConfirm: (reorderedCards: CardWithId[]) => {
                setMyDeck((prev) => [...reorderedCards, ...prev.slice(cardCount)]);
                addLog(`残りをデッキに戻した`, 'info');
              },
            });
          } else if (remainingCards.length === 1) {
            setMyDeck((prev) => [remainingCards[0], ...prev.slice(cardCount)]);
          } else {
            setMyDeck((prev) => prev.slice(cardCount));
          }
        },
        selectionOwner: context.currentPlayer,
      });

      return true;
    }

    // 基本技: 墓地の未来属性カードをデッキに戻し、レストSPをアクティブに
    if (context.skillType === 'basic') {
      const futureCard = myGraveyard.find(card => checkAttribute(card, '未来'));

      if (!futureCard) {
        addLog('墓地に未来属性カードがありません', 'info');
        return false;
      }

      // 墓地からデッキに戻す
      setMyGraveyard(prev => prev.filter(c => c.uniqueId !== futureCard.uniqueId));
      setMyDeck(prev => [...prev, futureCard]);
      addLog(`墓地から「${futureCard.name}」をデッキに戻した`, 'info');

      // レスト状態のSPをアクティブに
      if (myRestedSP > 0) {
        setMyRestedSP(prev => prev - 1);
        setMyActiveSP(prev => prev + 1);
        addLog('レスト状態のSPトークン1個をアクティブにした', 'info');
      } else {
        addLog('レスト状態のSPトークンがありません', 'info');
      }

      return true;
    }

    // 上級技: 勝負中1度のみ、デッキ上3枚から1枚を召喚（コスト無視）
    if (context.skillType === 'advanced') {
      const thisCard = myField[monsterIndex];
      if (!thisCard) {
        addLog('エラー: カードが見つかりません', 'info');
        return false;
      }

      // 勝負中1度のみチェック
      if (thisCard.usedUltimateSkillC0000271) {
        addLog('この効果は勝負中に1度しか使用できません', 'info');
        return false;
      }

      if (myDeck.length === 0) {
        addLog('デッキにカードがありません', 'info');
        return false;
      }

      const cardCount = Math.min(3, myDeck.length);
      const topCards = myDeck.slice(0, cardCount);

      addLog(`黄金の時姫エクラリア上級技: デッキ上${cardCount}枚を公開！`, 'info');
      topCards.forEach(card => addLog(`  - ${card.name}`, 'info'));

      // 空きスロットを確認
      const emptySlotIndex = myField.findIndex((slot, idx) => slot === null && idx !== monsterIndex);
      if (emptySlotIndex === -1) {
        addLog('フィールドに空きがありません', 'info');
        return false;
      }

      // 使用済みフラグを設定
      setMyField(prev => prev.map((m, idx) => {
        if (idx === monsterIndex && m) {
          return { ...m, usedUltimateSkillC0000271: true, pendingEndTurnSacrifice: true };
        }
        return m;
      }));

      // プレイヤーにカードを選択させる
      setPendingDeckReview({
        cards: topCards,
        title: '黄金の時姫エクラリア - 上級技',
        message: '場に召喚するカードを1枚選択してください（コスト無視）',
        allowReorder: false,
        selectMode: { enabled: true, count: 1 },
        onSelect: (selectedCards: CardWithId[], remainingCards: CardWithId[]) => {
          const cardToSummon = selectedCards[0];

          // 召喚処理
          if (cardToSummon.type === 'monster') {
            const monsterInstance = {
              ...cardToSummon,
              uniqueId: `${cardToSummon.id}_${Date.now()}`,
              currentHp: cardToSummon.hp ?? 0,
              currentAttack: cardToSummon.attack ?? 0,
              canAttack: false,
              usedSkillThisTurn: false,
              owner: currentPlayer,
              charges: [],
              statusEffects: [],
              summonedByEclaria: true, // ターン終了時の墓地送り対象マーク
            };

            setMyField(prev => prev.map((slot, idx) =>
              idx === emptySlotIndex ? monsterInstance : slot
            ));
            addLog(`${cardToSummon.name}を場に召喚！（コスト無視）`, 'info');
          } else {
            // モンスター以外の場合は墓地へ
            setMyGraveyard(prev => [...prev, cardToSummon]);
            addLog(`${cardToSummon.name}は召喚できないため墓地に送られた`, 'info');
          }

          // 残りカードをデッキに戻す
          setMyDeck((prev) => [...remainingCards, ...prev.slice(cardCount)]);
          if (remainingCards.length > 0) {
            addLog(`残り${remainingCards.length}枚をデッキに戻した`, 'info');
          }

          addLog('⚠️ ターン終了時、このカード以外のモンスターは墓地に送られます', 'damage');
        },
        selectionOwner: context.currentPlayer,
      });

      return true;
    }

    return false;
  },

  /**
   * C0000273: エクラリアの残影
   * 自分のデッキから《エクラリア》と名のついたモンスター1体を手札に加える。
   */
  C0000273: (skillText, context) => {
    const { addLog } = context;

    // デッキから「エクラリア」の名前を持つモンスターを検索
    const foundCard = searchCard(context, (card) => {
      return card.type === 'monster' && !!card.name && card.name.includes('エクラリア');
    });

    if (foundCard) {
      addLog(`エクラリアの残影: ${foundCard.name}を手札に加えた`, 'info');
    } else {
      addLog('エクラリアの残影: デッキに《エクラリア》モンスターがありません', 'info');
    }
    return true; // 効果処理は完了（対象の有無に関わらず）
  },

  /**
   * C0000258: 禁忌の鴉王ミラン
   * 基本技: 相手モンスター全体に500ダメージ。
   * 上級技：勝負中1度のみ使用可能、相手のデッキの上から2枚を「カラス人形（攻撃0、HP200、コスト2、未来属性）」に変換する。
   */
  C0000258: (skillText, context) => {
    const { addLog, monsterIndex, summoningSickness = false } = context;
    const { myField, setMyField, opponentField, setOpponentField, setOpponentGraveyard, opponentDeck, setOpponentDeck, currentPlayer } = getPlayerContext(context);

    // 基本技: 相手モンスター全体に500ダメージ
    if (context.skillType === 'basic') {
      const monsters = opponentField.filter(m => m !== null);
      if (monsters.length === 0) {
        addLog('相手フィールドにモンスターがいません', 'info');
        return true;
      }

      addLog('禁忌の鴉王ミランの基本技発動！', 'info');

      // 全モンスターに500ダメージ
      const destroyedMonsters: MonsterInstance[] = [];
      setOpponentField((prev) => {
        return prev.map((monster) => {
          if (monster) {
            const newHp = monster.currentHp - 500;
            addLog(`${monster.name}に500ダメージ（残りHP: ${Math.max(0, newHp)}）`, 'damage');
            if (newHp <= 0) {
              destroyedMonsters.push(monster);
              addLog(`${monster.name}は破壊された！`, 'damage');
              return null;
            }
            return { ...monster, currentHp: newHp };
          }
          return monster;
        });
      });

      // 破壊されたモンスターを墓地に送る
      if (destroyedMonsters.length > 0) {
        setOpponentGraveyard((prev) => [...prev, ...destroyedMonsters]);
      }

      return true;
    }

    // 上級技：勝負中1度のみ使用可能、相手のデッキの上から2枚を「カラス人形」に変換
    if (context.skillType === 'advanced') {
      const thisCard = myField[monsterIndex];
      if (!thisCard) {
        addLog('エラー: カードが見つかりません', 'info');
        return false;
      }

      // 勝負中1度のみチェック
      if (thisCard.usedUltimateSkillC0000258) {
        addLog('この効果は勝負中に1度しか使用できません', 'info');
        return false;
      }

      if (opponentDeck.length === 0) {
        addLog('相手のデッキにカードがありません', 'info');
        return false;
      }

      // 相手の空きスロットを確認
      const emptySlots = opponentField.reduce((acc: number[], slot, idx) => {
        if (slot === null) acc.push(idx);
        return acc;
      }, []);

      if (emptySlots.length === 0) {
        addLog('相手のフィールドに空きがありません', 'info');
        return false;
      }

      // 使用済みフラグを設定
      setMyField((prev) => prev.map((m, idx) => {
        if (idx === monsterIndex && m) {
          return { ...m, usedUltimateSkillC0000258: true };
        }
        return m;
      }));

      addLog('禁忌の鴉王ミランの上級技発動！', 'info');

      // デッキの上から最大2枚を取得
      const cardCount = Math.min(2, opponentDeck.length, emptySlots.length);
      const takenCards = opponentDeck.slice(0, cardCount);

      // デッキから削除
      setOpponentDeck((prev) => prev.slice(cardCount));

      // カラス人形を作成して相手の場に召喚
      const crowDolls = takenCards.map((originalCard, i) => {
        addLog(`${originalCard.name}がカラス人形に変換された！`, 'info');
        return {
          id: `CROW_DOLL_${Date.now()}_${i}`,
          uniqueId: `CROW_DOLL_${Date.now()}_${i}`,
          name: 'カラス人形',
          attribute: '未来' as const,
          cost: 2,
          type: 'monster' as const,
          attack: 0,
          hp: 200,
          currentAttack: 0,
          currentHp: 200,
          maxHp: 200,
          category: '【人形】',
          effect: 'このカードは禁忌の鴉王ミランの効果で生成された。',
          flavor: '',
          keyword: '',
          canAttack: !summoningSickness, // 召喚酔い設定に依存
          usedSkillThisTurn: false,
          owner: (currentPlayer === 1 ? 2 : 1) as PlayerNumber, // 相手の場に召喚するので相手が所有者
          charges: [],
          statusEffects: [],
          originalCard: originalCard, // 元のカード情報を保持
        };
      });

      // 相手の場に召喚
      setOpponentField((prev) => {
        const newField = [...prev];
        crowDolls.forEach((doll, i) => {
          if (emptySlots[i] !== undefined) {
            newField[emptySlots[i]] = doll;
            addLog(`カラス人形が相手の場に召喚された！`, 'info');
          }
        });
        return newField;
      });

      return true;
    }

    return false;
  },

  /**
   * C0000107: 守護の魔導ゴーレム
   * 基本技: SPトークン2個をレスト状態にし、自分のモンスター全体に「守護」を付与する
   * 上級技: 自分の手札1枚を墓地に送り、自分の墓地のモンスター1体を場に戻す（攻撃力半減、HP半減、効果無効）
   * ※トリガー（エンドフェイズ時のHP回復）はfutureCards.jsで実装済み
   */
  C0000107: (skillText, context) => {
    const { addLog, setPendingHandSelection, setPendingGraveyardSelection, summoningSickness = false } = context;
    const {
      myField,
      setMyField,
      myActiveSP,
      setMyActiveSP,
      setMyRestedSP,
      myHand,
      setMyHand,
      myGraveyard,
      setMyGraveyard,
      currentPlayer,
    } = getPlayerContext(context);

    // 基本技: SP2個レスト、全体に守護付与
    if (context.skillType === 'basic') {
      // SP確認
      if (myActiveSP < 2) {
        addLog('アクティブSPが2個以上必要です', 'info');
        return false;
      }

      // SP2個をレスト
      setMyActiveSP((prev) => prev - 2);
      setMyRestedSP((prev) => prev + 2);
      addLog('SPトークン2個をレスト状態にした', 'info');

      // 自分のモンスター全体に守護を付与
      let guardApplied = false;
      setMyField((prev) =>
        prev.map((monster) => {
          if (monster) {
            guardApplied = true;
            // 既存のstatusEffectsに守護を追加
            const newStatusEffects = [...(monster.statusEffects || [])];
            // 既に守護があれば追加しない
            if (!newStatusEffects.some((e) => e.type === 'GUARD')) {
              newStatusEffects.push({
                type: 'GUARD',
                duration: -1, // 1回使用で消費
                source: 'C0000107',
                sourceName: '守護の魔導ゴーレム',
              });
              addLog(`${monster.name}に守護を付与！`, 'info');
            }
            return { ...monster, statusEffects: newStatusEffects };
          }
          return monster;
        })
      );

      if (!guardApplied) {
        addLog('守護を付与する対象がいません', 'info');
      }

      return true;
    }

    // 上級技: 手札1枚捨て、墓地からモンスター蘇生（半減、効果無効）
    if (context.skillType === 'advanced') {
      // 手札確認
      if (myHand.length === 0) {
        addLog('手札がありません', 'info');
        return false;
      }

      // 墓地にモンスターがあるか確認
      const validMonsters = myGraveyard.filter((card) => card.type === 'monster');
      if (validMonsters.length === 0) {
        addLog('墓地にモンスターがいません', 'info');
        return false;
      }

      // 空きスロット確認
      const emptySlotIndex = myField.findIndex((slot) => slot === null);
      if (emptySlotIndex === -1) {
        addLog('場が満杯です', 'info');
        return false;
      }

      // 手札選択
      if (setPendingHandSelection) {
        setPendingHandSelection({
          message: '墓地に送るカードを選択',
          callback: (selectedCards: CardWithId[]) => {
            if (!selectedCards || selectedCards.length === 0) return;
            const selectedCard = selectedCards[0];
            // 手札から墓地へ
            setMyHand((prev) => prev.filter((c) => c.uniqueId !== selectedCard.uniqueId));
            setMyGraveyard((prev) => [...prev, selectedCard]);
            addLog(`${selectedCard.name}を墓地に送った`, 'info');

            // 墓地からモンスター選択
            const monstersInGraveyard = myGraveyard.filter((card) => card.type === 'monster');

            if (monstersInGraveyard.length === 1) {
              // 1体のみの場合は自動選択
              const targetCard = monstersInGraveyard[0];
              performRevive(targetCard);
            } else if (monstersInGraveyard.length > 1 && setPendingGraveyardSelection) {
              // 複数の場合は選択
              setPendingGraveyardSelection({
                message: '蘇生するモンスターを選択（攻撃力・HP半減、効果無効）',
                cards: monstersInGraveyard,
                callback: (selectedCards: CardWithId[]) => {
                  if (selectedCards[0]) performRevive(selectedCards[0]);
                },
                selectionOwner: context.currentPlayer,
              });
            }

            function performRevive(targetCard: CardWithId) {
              // 墓地から削除
              setMyGraveyard((prev) => prev.filter((c) => c.uniqueId !== targetCard.uniqueId));

              // 場に蘇生（攻撃力・HP半減、効果無効）
              const halfAttack = Math.floor((targetCard.attack || 0) / 2);
              const halfHp = Math.floor((targetCard.hp || 0) / 2);

              setMyField((prev) => {
                const newField = [...prev];
                const slot = newField.findIndex((s) => s === null);
                if (slot !== -1) {
                  let revivedMonster = {
                    ...targetCard,
                    currentAttack: halfAttack,
                    attack: halfAttack,
                    currentHp: halfHp,
                    maxHp: halfHp,
                    hp: halfHp,
                    canAttack: !summoningSickness, // 召喚酔い設定に依存
                    owner: currentPlayer,
                    charges: [],
                    statusEffects: [],
                    usedSkillThisTurn: false,
                  };
                  // SILENCE状態異常を付与（永続）
                  statusEffectEngine.applyStatus(revivedMonster, STATUS_EFFECT_TYPES.SILENCE, {
                    duration: -1, // 永続
                    source: 'C0000107',
                    sourceName: '守護の魔導ゴーレム',
                  });
                  newField[slot] = revivedMonster as MonsterInstance;
                  addLog(
                    `${targetCard.name}を蘇生！（ATK: ${halfAttack}, HP: ${halfHp}, 効果無効）`,
                    'info'
                  );
                }
                return newField;
              });
            }
          },
          selectionOwner: context.currentPlayer,
        });
        return true;
      }

      return false;
    }

    return false;
  },

  // ========================================
  // フェイズカード
  // ========================================

  /**
   * C0000371: 風詠の鳥民・ゼフィラス
   * 【召喚時】自分のデッキの上から3枚を見て、［ウィング・トリビューン］ もしくは
   * 《ヴォランティス》カード1枚を手札に加え、残りを好きな順番でデッキの下に戻す。
   * 基本技: 手札のモンスターカードを1枚捨て1枚ドローする（この効果はターンに1度のみ）。
   */
  C0000371: (skillText, context) => {
    const { addLog, setPendingDeckReview, setPendingHandSelection } = context;
    const {
      myDeck, setMyDeck, setMyHand, myHand, setMyGraveyard,
    } = getPlayerContext(context);

    // 召喚時効果: デッキトップ3枚から1枚選択
    if (skillText.includes('【召喚時】')) {
      if (myDeck.length === 0) {
        addLog('ゼフィラス: デッキが空です', 'info');
        return false;
      }

      // デッキの上から3枚を取得
      const topCards = myDeck.slice(0, Math.min(3, myDeck.length));

      // ［ウィング・トリビューン］または《ヴォランティス》カードをフィルタ
      const validCards = topCards.filter(card =>
        (card.category && card.category.includes('ウィング・トリビューン')) ||
        (card.name && card.name.includes('ヴォランティス'))
      );

      if (validCards.length === 0) {
        // 対象がない場合、3枚をデッキの下に戻す
        addLog('ゼフィラス【召喚時】: 対象となるカードがありませんでした', 'info');
        setMyDeck(prev => {
          const remaining = prev.slice(Math.min(3, prev.length));
          return [...remaining, ...topCards];
        });
        return true;
      }

      // 選択UI
      setPendingDeckReview({
        message: '手札に加える［ウィング・トリビューン］または《ヴォランティス》カードを選択',
        cards: validCards,
        count: 1,
        callback: (selectedCards: CardWithId[]) => {
          if (selectedCards && selectedCards.length > 0) {
            const selectedCard = selectedCards[0];

            // 選択したカードを手札に加える
            setMyHand(prev => [...prev, selectedCard]);
            addLog(`ゼフィラス【召喚時】: ${selectedCard.name}を手札に加えた！`, 'info');

            // 残りをデッキの下に戻す
            setMyDeck(prev => {
              const topThree = prev.slice(0, Math.min(3, prev.length));
              const remaining = prev.slice(Math.min(3, prev.length));
              const toBottom = topThree.filter(c => c.uniqueId !== selectedCard.uniqueId);
              return [...remaining, ...toBottom];
            });
            addLog('ゼフィラス【召喚時】: 残りのカードをデッキの下に戻した', 'info');
          } else {
            // キャンセル時も3枚を下に戻す
            setMyDeck(prev => {
              const remaining = prev.slice(Math.min(3, prev.length));
              return [...remaining, ...topCards];
            });
          }
        },
        selectionOwner: context.currentPlayer,
      });
      return true;
    }

    // 基本技: 手札1枚捨て、1枚ドロー
    if (context.skillType === 'basic') {
      // 手札にモンスターカードがあるか確認
      const monsterCards = myHand.filter(card => card.type === 'monster');
      if (monsterCards.length === 0) {
        addLog('ゼフィラスの基本技: 手札にモンスターカードがありません', 'info');
        return false;
      }

      // デッキが空なら失敗
      if (myDeck.length === 0) {
        addLog('ゼフィラスの基本技: デッキが空です', 'info');
        return false;
      }

      // 手札選択
      setPendingHandSelection({
        message: '墓地に捨てるモンスターカードを選択',
        cards: monsterCards,
        count: 1,
        callback: (selectedCards: CardWithId[]) => {
          if (selectedCards && selectedCards.length > 0) {
            const discardCard = selectedCards[0];

            // 手札から墓地へ
            setMyHand(prev => prev.filter(c => c.uniqueId !== discardCard.uniqueId));
            setMyGraveyard(prev => [...prev, discardCard]);
            addLog(`ゼフィラスの基本技: ${discardCard.name}を墓地に捨てた`, 'info');

            // 1枚ドロー
            const currentDeck = myDeck; // Note: closure reference
            if (currentDeck.length > 0) {
              setMyDeck(prev => {
                if (prev.length > 0) {
                  const drawnCard = prev[0];
                  setMyHand(hand => [...hand, drawnCard]);
                  addLog(`ゼフィラスの基本技: ${drawnCard.name}をドロー！`, 'info');
                  return prev.slice(1);
                }
                return prev;
              });
            }
          }
        },
        selectionOwner: context.currentPlayer,
      });
      return true;
    }

    return false;
  },

  /**
   * C0000266: エクラシアの時空炉（フェイズカード）
   * 3枚重ね: 自分の「未来属性」モンスター全体の攻撃力をターン終了時まで2000アップし、
   * 相手モンスター全体の攻撃力を1500ダウン。
   * その後、このカードを墓地に送り、次のターン開始時、自分に2000ダメージ。
   */
  C0000266: (skillText, context) => {
    const { addLog } = context;
    const { myField, setMyField, opponentField, setOpponentField, setMyLife } = getPlayerContext(context);

    if (skillText.includes('3枚重ね') || skillText.includes('３枚重ね')) {
      // 自分の未来属性モンスター全体のATK+2000（ターン終了時まで）
      let buffedCount = 0;
      setMyField(prev => {
        return prev.map(monster => {
          if (monster && monster.attribute === '未来') {
            buffedCount++;
            const newAttack = (monster.currentAttack ?? monster.attack ?? 0) + 2000;
            return {
              ...monster,
              currentAttack: newAttack,
              attackBuffUntilEndOfTurn: (monster.attackBuffUntilEndOfTurn || 0) + 2000,
            };
          }
          return monster;
        });
      });

      if (buffedCount > 0) {
        addLog(`エクラシアの時空炉【3枚重ね】: 未来属性モンスター${buffedCount}体の攻撃力+2000！`, 'info');
      }

      // 相手モンスター全体のATK-1500
      let debuffedCount = 0;
      setOpponentField(prev => {
        return prev.map(monster => {
          if (monster) {
            debuffedCount++;
            const newAttack = Math.max(0, (monster.currentAttack ?? monster.attack ?? 0) - 1500);
            return {
              ...monster,
              currentAttack: newAttack,
            };
          }
          return monster;
        });
      });

      if (debuffedCount > 0) {
        addLog(`エクラシアの時空炉【3枚重ね】: 相手モンスター${debuffedCount}体の攻撃力-1500！`, 'damage');
      }

      // 次のターン開始時に2000ダメージ
      // Note: 実際のダメージは即座に適用（遅延ダメージシステムがないため）
      addLog('エクラシアの時空炉【3枚重ね】: 次のターン開始時に自分に2000ダメージ！', 'damage');
      setMyLife(prev => prev - 2000);

      return true;
    }

    return false;
  },

  /**
   * C0000186: 時空偽造者・ギズネック
   * 基本技: SPトークン2個をレスト状態にし、コスト4以下の相手モンスター1体を手札に戻す。
   * 上級技: チャージ１つを消費して、相手フィールド全体に800ダメージを与える。
   */
  C0000186: (skillText, context) => {
    const { addLog, monsterIndex, setPendingMonsterTarget } = context;
    const {
      myActiveSP,
      setMyActiveSP,
      setMyRestedSP,
      myField,
      setMyField,
      opponentField,
      setOpponentField,
      setOpponentHand,
      setOpponentGraveyard,
    } = getPlayerContext(context);

    // 基本技: SP2個レスト、コスト4以下の相手モンスターをバウンス
    if (context.skillType === 'basic') {
      // SP確認
      if (myActiveSP < 2) {
        addLog('時空偽造者・ギズネック: アクティブSPが2個以上必要です', 'info');
        return false;
      }

      // コスト4以下の相手モンスターを検索
      const validTargets = opponentField
        .map((m, idx) => ({ monster: m, index: idx }))
        .filter(({ monster }) => monster !== null && monster.cost <= 4);

      if (validTargets.length === 0) {
        addLog('時空偽造者・ギズネック: コスト4以下の相手モンスターがいません', 'info');
        return false;
      }

      // SP2個をレスト
      setMyActiveSP((prev) => prev - 2);
      setMyRestedSP((prev) => prev + 2);
      addLog('時空偽造者・ギズネック: SPトークン2個をレスト状態にした', 'info');

      // ターゲット選択
      setPendingMonsterTarget({
        message: '手札に戻すコスト4以下の相手モンスターを選択',
        validTargets: validTargets.map((t) => t.index),
        targetPlayer: 'opponent',
        callback: (targetIndex: number) => {
          const target = opponentField[targetIndex];
          if (!target) return;

          // 手札に戻す
          setOpponentField((prev) => {
            const newField = [...prev];
            newField[targetIndex] = null;
            return newField;
          });
          setOpponentHand((prev) => [...prev, target]);
          addLog(`時空偽造者・ギズネック: ${target.name}を手札に戻した！`, 'info');
        },
        selectionOwner: context.currentPlayer,
      });
      return true;
    }

    // 上級技: チャージ1つ消費で相手全体に800ダメージ
    if (context.skillType === 'advanced') {
      const thisCard = myField[monsterIndex];
      if (!thisCard) {
        addLog('時空偽造者・ギズネック: カードが見つかりません', 'info');
        return false;
      }

      // チャージ確認
      const charges = thisCard.charges || [];
      if (charges.length === 0) {
        addLog('時空偽造者・ギズネック: チャージがありません', 'info');
        return false;
      }

      // チャージ1つ消費
      setMyField((prev) =>
        prev.map((m, idx) => {
          if (idx === monsterIndex && m) {
            const newCharges = [...(m.charges || [])];
            newCharges.pop();
            return { ...m, charges: newCharges };
          }
          return m;
        })
      );

      addLog('時空偽造者・ギズネック: チャージを1つ消費！', 'info');

      // 相手フィールド全体に800ダメージ
      const destroyedMonsters: MonsterInstance[] = [];
      setOpponentField((prev) =>
        prev.map((monster) => {
          if (monster) {
            const newHp = monster.currentHp - 800;
            addLog(`${monster.name}に800ダメージ！（残りHP: ${Math.max(0, newHp)}）`, 'damage');
            if (newHp <= 0) {
              destroyedMonsters.push(monster);
              addLog(`${monster.name}は破壊された！`, 'damage');
              return null;
            }
            return { ...monster, currentHp: newHp };
          }
          return monster;
        })
      );

      // 破壊されたモンスターを墓地へ
      if (destroyedMonsters.length > 0) {
        setOpponentGraveyard((prev) => [...prev, ...destroyedMonsters]);
      }

      return true;
    }

    return false;
  },

  /**
   * C0000272: 虚蝕の崩姫エクラリア【禁忌カード】
   * 【召喚時】相手のデッキ上5枚を墓地に送り、その中のモンスター1体につき500ダメージを相手プレイヤーに与える。
   * 【自壊時】自分のライフを2500減らす。（トリガーで実装）
   * 基本技: 相手モンスター1体の攻撃力をターン終了時まで1000下げ、そのモンスターが場を離れた時、相手プレイヤーに600ダメージ。
   * 上級技: 勝負中1度のみ、自分の墓地の「未来属性」カードを3枚をデッキに戻し、その後、相手フィールド全体に3000ダメージを与える。
   */
  C0000272: (skillText, context) => {
    const { addLog, monsterIndex, setPendingMonsterTarget } = context;
    const { myField, setMyField, myGraveyard, setMyGraveyard, setMyDeck, opponentField, setOpponentField, setOpponentGraveyard } = getPlayerContext(context);

    // 召喚時効果
    if (skillText.includes('【召喚時】')) {
      const milledCards = millOpponentDeck(context, 5);

      if (milledCards.length === 0) {
        addLog('虚蝕の崩姫エクラリア: 相手のデッキにカードがありません', 'info');
        return true;
      }

      // 墓地に送ったカードを表示
      addLog(`虚蝕の崩姫エクラリアの効果: 相手のデッキから${milledCards.length}枚を墓地に送った`, 'info');
      milledCards.forEach(card => addLog(`  - ${card.name}`, 'info'));

      // モンスター数をカウント
      const monsterCount = milledCards.filter(card => card.type === 'monster').length;

      if (monsterCount > 0) {
        const totalDamage = monsterCount * 500;
        conditionalDamage(context, totalDamage, 'opponent');
        addLog(`モンスター${monsterCount}体分、${totalDamage}ダメージ！`, 'damage');
      } else {
        addLog('墓地に送ったカードにモンスターはいませんでした', 'info');
      }

      return true;
    }

    // 基本技: 相手モンスター1体のATK-1000（ターン終了まで）、破壊時600ダメージ
    if (context.skillType === 'basic') {
      const validTargets = opponentField
        .map((m, idx) => ({ monster: m, index: idx }))
        .filter(({ monster }) => monster !== null);

      if (validTargets.length === 0) {
        addLog('虚蝕の崩姫エクラリア: 相手フィールドにモンスターがいません', 'info');
        return false;
      }

      const applyDebuff = (targetIndex: number) => {
        setOpponentField(prev => prev.map((m, idx) => {
          if (idx === targetIndex && m) {
            const newAtk = Math.max(0, (m.currentAttack ?? m.attack ?? 0) - 1000);
            addLog(`虚蝕の崩姫エクラリアの基本技: ${m.name}の攻撃力を1000ダウン（${newAtk}）！`, 'damage');
            return {
              ...m,
              currentAttack: newAtk,
              attackDebuffUntilEndOfTurn: (m.attackDebuffUntilEndOfTurn || 0) + 1000,
              // 破壊時600ダメージのマーカー
              onDestroyDamageToOpponent: 600,
              onDestroyDamageSource: '虚蝕の崩姫エクラリア',
            };
          }
          return m;
        }));
        addLog('※このモンスターが場を離れた時、相手に600ダメージ！', 'info');
      };

      if (validTargets.length === 1) {
        applyDebuff(validTargets[0].index);
        return true;
      }

      setPendingMonsterTarget({
        message: '攻撃力を1000下げる相手モンスターを選択',
        targetPlayer: 'opponent',
        validTargets: validTargets.map(t => t.index),
        callback: (selectedIndex: number) => {
          applyDebuff(selectedIndex);
        },
        selectionOwner: context.currentPlayer,
      });
      return true;
    }

    // 上級技: 勝負中1度、墓地の未来属性3枚デッキ戻し、相手全体に3000ダメージ
    if (context.skillType === 'advanced') {
      const thisCard = myField[monsterIndex];
      if (!thisCard) {
        addLog('虚蝕の崩姫エクラリア: カードが見つかりません', 'info');
        return false;
      }

      // 勝負中1度のみチェック
      if (thisCard.usedUltimateSkillC0000272) {
        addLog('虚蝕の崩姫エクラリア: この効果は勝負中に1度しか使用できません', 'info');
        return false;
      }

      // 墓地から未来属性カードを3枚確認
      const futureCards = myGraveyard.filter(card => checkAttribute(card, '未来'));
      if (futureCards.length < 3) {
        addLog(`虚蝕の崩姫エクラリア: 墓地に未来属性カードが${futureCards.length}枚しかありません（3枚必要）`, 'info');
        return false;
      }

      // 使用済みフラグを設定
      setMyField(prev => prev.map((m, idx) => {
        if (idx === monsterIndex && m) {
          return { ...m, usedUltimateSkillC0000272: true };
        }
        return m;
      }));

      // 墓地から3枚をデッキに戻す（最初の3枚）
      const cardsToReturn = futureCards.slice(0, 3);
      setMyGraveyard(prev => prev.filter(c => !cardsToReturn.some(r => r.uniqueId === c.uniqueId)));
      setMyDeck(prev => [...prev, ...cardsToReturn]);

      addLog(`虚蝕の崩姫エクラリアの上級技発動！`, 'info');
      cardsToReturn.forEach(card => addLog(`  ${card.name}をデッキに戻した`, 'info'));

      // 相手フィールド全体に3000ダメージ
      const destroyedMonsters: MonsterInstance[] = [];
      setOpponentField(prev => prev.map(monster => {
        if (monster) {
          const newHp = (monster.currentHp ?? monster.hp ?? 0) - 3000;
          addLog(`${monster.name}に3000ダメージ！（残りHP: ${Math.max(0, newHp)}）`, 'damage');
          if (newHp <= 0) {
            destroyedMonsters.push(monster);
            addLog(`${monster.name}は破壊された！`, 'damage');
            return null;
          }
          return { ...monster, currentHp: newHp };
        }
        return monster;
      }));

      if (destroyedMonsters.length > 0) {
        setOpponentGraveyard(prev => [...prev, ...destroyedMonsters]);
      }

      if (opponentField.filter(m => m !== null).length === 0) {
        addLog('相手フィールドにモンスターがいません', 'info');
      }

      return true;
    }

    return false;
  },

  /**
   * C0000276: 大崩壊の刻
   * 自分のデッキ上3枚を墓地に送り、その中の「未来属性」カード1枚につき自分のレスト状態のSPトークンをアクティブにする。
   * その後、場にいるモンスター全体の攻撃力をターン終了時まで1000下げ、場のモンスター全体に1500ダメージを与える。
   */
  C0000276: (skillText, context) => {
    const { addLog } = context;
    const { myDeck, setMyDeck, myGraveyard, setMyGraveyard, myRestedSP, setMyRestedSP, setMyActiveSP, myField, setMyField, opponentField, setOpponentField, setOpponentGraveyard } = getPlayerContext(context);

    // デッキ上3枚を墓地に送る
    const cardCount = Math.min(3, myDeck.length);
    if (cardCount === 0) {
      addLog('大崩壊の刻: デッキにカードがありません', 'info');
      return true;
    }

    const milledCards = myDeck.slice(0, cardCount);
    setMyDeck(prev => prev.slice(cardCount));
    setMyGraveyard(prev => [...prev, ...milledCards]);

    addLog(`大崩壊の刻: デッキから${cardCount}枚を墓地に送った`, 'info');
    milledCards.forEach(card => addLog(`  - ${card.name}`, 'info'));

    // 未来属性カード数をカウント
    const futureCount = milledCards.filter(card => checkAttribute(card, '未来')).length;

    // レストSPをアクティブに
    const spToActivate = Math.min(futureCount, myRestedSP);
    if (spToActivate > 0) {
      setMyRestedSP(prev => prev - spToActivate);
      setMyActiveSP(prev => prev + spToActivate);
      addLog(`未来属性${futureCount}枚！SPトークン${spToActivate}個をアクティブにした`, 'info');
    } else if (futureCount > 0) {
      addLog(`未来属性${futureCount}枚だがレスト状態のSPがありません`, 'info');
    }

    // 全モンスターのATK-1000（ターン終了まで）
    addLog('大崩壊の刻: 全モンスターの攻撃力を1000ダウン！', 'damage');

    setMyField(prev => prev.map(monster => {
      if (monster) {
        const newAtk = Math.max(0, (monster.currentAttack ?? monster.attack ?? 0) - 1000);
        return {
          ...monster,
          currentAttack: newAtk,
          attackDebuffUntilEndOfTurn: (monster.attackDebuffUntilEndOfTurn || 0) + 1000,
        };
      }
      return monster;
    }));

    // 全モンスターに1500ダメージ
    addLog('大崩壊の刻: 全モンスターに1500ダメージ！', 'damage');

    const destroyedOpponentMonsters: MonsterInstance[] = [];
    const destroyedMyMonsters: MonsterInstance[] = [];

    setOpponentField(prev => prev.map(monster => {
      if (monster) {
        const newAtk = Math.max(0, (monster.currentAttack ?? monster.attack ?? 0) - 1000);
        const newHp = (monster.currentHp ?? monster.hp ?? 0) - 1500;
        addLog(`${monster.name}: ATK ${newAtk}、HP ${Math.max(0, newHp)}`, 'damage');
        if (newHp <= 0) {
          destroyedOpponentMonsters.push(monster);
          return null;
        }
        return {
          ...monster,
          currentAttack: newAtk,
          currentHp: newHp,
          attackDebuffUntilEndOfTurn: (monster.attackDebuffUntilEndOfTurn || 0) + 1000,
        };
      }
      return monster;
    }));

    setMyField(prev => prev.map(monster => {
      if (monster) {
        const newHp = (monster.currentHp ?? monster.hp ?? 0) - 1500;
        addLog(`${monster.name}: HP ${Math.max(0, newHp)}`, 'damage');
        if (newHp <= 0) {
          destroyedMyMonsters.push(monster);
          return null;
        }
        return { ...monster, currentHp: newHp };
      }
      return monster;
    }));

    if (destroyedOpponentMonsters.length > 0) {
      setOpponentGraveyard(prev => [...prev, ...destroyedOpponentMonsters]);
      destroyedOpponentMonsters.forEach(m => addLog(`${m.name}は破壊された！`, 'damage'));
    }

    if (destroyedMyMonsters.length > 0) {
      setMyGraveyard(prev => [...prev, ...destroyedMyMonsters]);
      destroyedMyMonsters.forEach(m => addLog(`${m.name}は破壊された！`, 'damage'));
    }

    return true;
  },

  /**
   * C0000253: 未来の紫翼
   * デッキからコスト3以下の「未来属性」モンスター1体を場に召喚。
   * このターン、自分の「未来属性」モンスターが相手プレイヤーに与えるダメージを500アップ。
   */
  C0000253: (skillText, context) => {
    const { addLog, setPendingDeckReview, currentPlayer, summoningSickness = false } = context;
    const { myDeck, myField, setMyDeck, setMyField } = getPlayerContext(context);

    // 空きスロットを確認
    const emptySlotIndex = myField.findIndex(slot => slot === null);
    if (emptySlotIndex === -1) {
      addLog('未来の紫翼: 場が満杯で召喚できません', 'info');
      return false;
    }

    // デッキからコスト3以下の未来モンスターを検索
    const validCards = myDeck.filter(card =>
      card.type === 'monster' && card.attribute === '未来' && card.cost <= 3
    );

    if (validCards.length === 0) {
      addLog('未来の紫翼: デッキにコスト3以下の未来属性モンスターがいません', 'info');
      return false;
    }

    const summonMonster = (selectedCard: CardWithId) => {
      // デッキから除去
      setMyDeck(prev => prev.filter(c => c.uniqueId !== selectedCard.uniqueId));

      // 召喚
      setMyField(prev => {
        const newField = [...prev];
        const slot = newField.findIndex(s => s === null);
        if (slot !== -1) {
          newField[slot] = {
            ...selectedCard,
            uniqueId: `${selectedCard.id}_summoned_${Date.now()}`,
            currentAttack: selectedCard.attack ?? 0,
            currentHp: selectedCard.hp ?? 0,
            canAttack: !summoningSickness, // 召喚酔い設定に依存
            charges: [],
            statusEffects: [],
            owner: currentPlayer,
          };
        }
        return newField;
      });
      addLog(`未来の紫翼: 「${selectedCard.name}」を召喚！`, 'info');

      // 未来属性モンスターのダメージ+500（ターン終了時まで）
      setMyField(prev => prev.map(m => {
        if (m && m.attribute === '未来') {
          return { ...m, damageBuffUntilEndOfTurn: (m.damageBuffUntilEndOfTurn || 0) + 500 };
        }
        return m;
      }));
      addLog('未来の紫翼: このターン、未来属性モンスターの与ダメージ+500！', 'info');
    };

    // 複数いる場合は選択UI
    if (validCards.length > 1 && setPendingDeckReview) {
      setPendingDeckReview({
        cards: validCards,
        message: '召喚するコスト3以下の未来属性モンスターを選択',
        count: 1,
        callback: (selectedCards: CardWithId[]) => {
          if (selectedCards && selectedCards.length > 0) {
            summonMonster(selectedCards[0]);
          }
        },
        selectionOwner: context.currentPlayer,
      });
      return true;
    }

    // 1体のみの場合は自動選択
    summonMonster(validCards[0]);
    return true;
  },

  /**
   * C0000254: 時の歪曲
   * 【刹那詠唱】
   * 相手モンスター1体の攻撃を無効化し、次の相手ターン終了時まで「凍結」状態にする（行動不能、効果無効）。
   * その間、そのモンスターの攻撃力を0にする。
   * 場に《紫鴉の魔導士ミラン》がいる場合、対象モンスターを破壊し、相手プレイヤーに破壊したモンスターの元の攻撃力の半分のダメージを与える。
   */
  C0000254: (skillText, context) => {
    const { addLog, setPendingMonsterTarget } = context;
    const { myField, opponentField, setOpponentField, setOpponentGraveyard, setOpponentLife, opponentLife } = getPlayerContext(context);

    // 相手フィールドにモンスターがいるか確認
    const opponentMonsters = opponentField
      .map((m, idx) => ({ monster: m, index: idx }))
      .filter(({ monster }) => monster !== null);

    if (opponentMonsters.length === 0) {
      addLog('時の歪曲: 相手フィールドにモンスターがいません', 'info');
      return false;
    }

    // ミランがいるか確認
    const hasMiran = myField.some(m => m && m.name && m.name.includes('紫鴉の魔導士ミラン'));

    const applyEffect = (targetIndex: number) => {
      const targetMonster = opponentField[targetIndex];
      if (!targetMonster) return;

      // ミランがいれば破壊してダメージ
      if (hasMiran) {
        const originalAttack = targetMonster.attack || 0;
        const damage = Math.floor(originalAttack / 2);

        addLog('時の歪曲: 《紫鴉の魔導士ミラン》がいるため、対象を破壊！', 'info');
        addLog(`時の歪曲: ${targetMonster.name}を破壊！`, 'damage');

        // モンスターを破壊
        setOpponentField(prev => {
          const newField = [...prev];
          newField[targetIndex] = null;
          return newField;
        });

        // 墓地に送る
        setOpponentGraveyard(prev => [...prev, targetMonster]);

        // 元の攻撃力の半分のダメージ
        if (damage > 0) {
          setOpponentLife(prev => {
            const newLife = prev - damage;
            addLog(`時の歪曲: 相手プレイヤーに${damage}ダメージ！（元の攻撃力${originalAttack}の半分）`, 'damage');
            return newLife;
          });
        }
      } else {
        // 凍結状態を付与（次の相手ターン終了時まで）
        setOpponentField(prev => {
          const newField = [...prev];
          if (newField[targetIndex]) {
            const originalAttack = newField[targetIndex].currentAttack || newField[targetIndex].attack;
            newField[targetIndex] = {
              ...newField[targetIndex],
              currentAttack: 0,
              originalAttackBeforeFreeze: originalAttack, // 元の攻撃力を保存
              statusEffects: [
                ...(newField[targetIndex].statusEffects || []),
                {
                  type: 'FREEZE',
                  source: 'C0000254',
                  sourceName: '時の歪曲',
                  duration: -1, // 次の相手ターン終了時まで
                  untilOpponentTurnEnd: true,
                },
              ],
              canAttack: false,
              effectNegated: true,
              freezeUntilOpponentTurnEnd: true,
            };
            addLog(`時の歪曲: ${newField[targetIndex].name}を凍結！（行動不能、効果無効、攻撃力0）`, 'info');
          }
          return newField;
        });
      }
    };

    // 1体のみの場合は自動選択
    if (opponentMonsters.length === 1) {
      applyEffect(opponentMonsters[0].index);
      return true;
    }

    // 複数いる場合は選択UI
    setPendingMonsterTarget({
      message: '凍結させる相手モンスターを選択',
      targetPlayer: 'opponent',
      callback: applyEffect,
      selectionOwner: context.currentPlayer,
    });

    return true;
  },

  /**
   * C0000259: 素晴らしい未来への誘導
   * 次の相手ターン開始時、相手が引くカードを1枚見て、デッキの上か下に置く。
   * そのターン、自分の『未来』モンスターの攻撃力を500アップ。
   */
  C0000259: (skillText, context) => {
    const { addLog } = context;
    const { myField, setMyField, setOpponentDeck, opponentDeck } = getPlayerContext(context);

    // 未来モンスターの攻撃力を500アップ（ターン終了時まで）
    let buffedCount = 0;
    setMyField(prev => prev.map(m => {
      if (m && m.attribute === '未来') {
        buffedCount++;
        return {
          ...m,
          currentAttack: (m.currentAttack ?? m.attack ?? 0) + 500,
          atkBuffedUntilEndOfTurn: true,
        };
      }
      return m;
    }));

    if (buffedCount > 0) {
      addLog(`素晴らしい未来への誘導: 未来属性モンスター${buffedCount}体の攻撃力+500！`, 'info');
    }

    // 相手デッキトップを確認して上下に置く効果（簡易実装：デッキボトムに置く）
    if (opponentDeck && opponentDeck.length > 0) {
      const topCard = opponentDeck[0];
      addLog(`素晴らしい未来への誘導: 相手デッキトップ「${topCard.name}」を確認、デッキボトムへ送った！`, 'info');
      setOpponentDeck(prev => {
        if (prev.length <= 1) return prev;
        return [...prev.slice(1), prev[0]];
      });
    }

    return true;
  },

  /**
   * C0000261: アクセラレート
   * 自分の次のターン開始時まで、自分の『未来』モンスターの基本技を2回使用可能。
   * 次の自分ターン開始時、１つSPトークンをレスト状態にする。
   */
  C0000261: (skillText, context) => {
    const { addLog } = context;
    const { myField, setMyField } = getPlayerContext(context);

    // 未来モンスターに基本技2回使用可能フラグを付与
    let buffedCount = 0;
    setMyField(prev => prev.map(m => {
      if (m && m.attribute === '未来') {
        buffedCount++;
        return {
          ...m,
          doubleBasicSkillUntilNextTurn: true,
        };
      }
      return m;
    }));

    if (buffedCount > 0) {
      addLog(`アクセラレート: 未来属性モンスター${buffedCount}体の基本技が2回使用可能に！`, 'info');
      addLog('アクセラレート: 次のターン開始時、SP1つがレスト状態になります', 'info');
    } else {
      addLog('アクセラレート: 場に未来属性モンスターがいません', 'info');
    }

    // 次ターン開始時のSPレスト効果は別途トリガーで実装が必要
    // ここでは警告のみ

    return true;
  },

  /**
   * C0000262: 時の遅延
   * 相手モンスター全体に800ダメージを与える。
   * 次の相手ターン終了まで、相手は基本技・上級技・魔法カードを使用できない。
   * 場に『未来』属性モンスターがいる場合、相手モンスター1体を手札に戻す。
   */
  C0000262: (skillText, context) => {
    const { addLog, setPendingMonsterTarget } = context;
    const { myField, opponentField, setOpponentField, setOpponentGraveyard, setOpponentHand } = getPlayerContext(context);

    // 1. 相手モンスター全体に800ダメージ
    let destroyedMonsters: MonsterInstance[] = [];
    setOpponentField(prev => {
      const newField = prev.map((m, idx) => {
        if (m) {
          const newHp = (m.currentHp ?? m.hp ?? 0) - 800;
          if (newHp <= 0) {
            destroyedMonsters.push(m);
            addLog(`時の遅延: ${m.name}に800ダメージ！破壊された！`, 'damage');
            return null;
          }
          addLog(`時の遅延: ${m.name}に800ダメージ！（残りHP: ${newHp}）`, 'damage');
          return { ...m, currentHp: newHp };
        }
        return m;
      });
      return newField;
    });

    // 破壊されたモンスターを墓地へ
    if (destroyedMonsters.length > 0) {
      setOpponentGraveyard(prev => [...prev, ...destroyedMonsters]);
    }

    // 2. 次の相手ターン終了まで、相手は基本技・上級技・魔法カードを使用できない
    // これはプレイヤー状態として管理する必要がある
    // 相手のモンスターに技封印 + 相手プレイヤーに魔法封印フラグを設定
    setOpponentField(prev => prev.map(m => {
      if (m) {
        return {
          ...m,
          skillSealedUntilOpponentTurnEnd: true,
        };
      }
      return m;
    }));

    addLog('時の遅延: 次の相手ターン終了まで、相手は基本技・上級技・魔法カードを使用できない！', 'info');

    // 魔法封印はcontext経由でプレイヤー状態に設定する必要があるが、
    // 現在のアーキテクチャでは直接設定が難しいため、フラグとしてログに記録
    // TODO: プレイヤー状態への魔法封印フラグ追加が必要

    // 3. 未来モンスターがいれば相手モンスター1体を手札に戻す
    const hasFutureMonster = myField.some(m => m && m.attribute === '未来');
    if (hasFutureMonster) {
      // 現在のフィールド状態を取得（800ダメージ後）
      const currentOpponentMonsters = opponentField
        .map((m, idx) => ({ monster: m, index: idx }))
        .filter(({ monster }) => monster !== null);

      // 破壊されたモンスターを除外した残りのモンスターを対象にする
      // setOpponentFieldがまだ反映されていないので、destroyedMonstersで除外
      const remainingMonsters = currentOpponentMonsters.filter(({ monster }) =>
        monster && !destroyedMonsters.some(d => d.uniqueId === monster.uniqueId)
      );

      if (remainingMonsters.length === 0) {
        addLog('時の遅延: 手札に戻せる相手モンスターがいません', 'info');
      } else if (remainingMonsters.length === 1) {
        const target = remainingMonsters[0];
        setOpponentField(prev => {
          const newField = [...prev];
          const monster = newField[target.index];
          if (monster) {
            newField[target.index] = null;
            addLog(`時の遅延: ${monster.name}を手札に戻した！`, 'info');
            // 手札に戻す
            setOpponentHand(prevHand => [...prevHand, monster]);
          }
          return newField;
        });
      } else if (setPendingMonsterTarget) {
        setPendingMonsterTarget({
          message: '手札に戻す相手モンスターを選択',
          targetPlayer: 'opponent',
          callback: (selectedIndex: number) => {
            setOpponentField(prev => {
              const newField = [...prev];
              const monster = newField[selectedIndex];
              if (monster) {
                newField[selectedIndex] = null;
                addLog(`時の遅延: ${monster.name}を手札に戻した！`, 'info');
                setOpponentHand(prevHand => [...prevHand, monster]);
              }
              return newField;
            });
          },
          selectionOwner: context.currentPlayer,
        });
      }
    }

    return true;
  },

  /**
   * C0000264: 時の逆流
   * 墓地に『未来』属性カード3枚あるとき発動可能。
   * 墓地の『未来』属性カード3枚をデッキに戻し、2枚ドローする。
   * 自分のライフを1500回復。
   * 自分のレスト状態のSPトークンを全てアクティブにする。
   */
  C0000264: (skillText, context) => {
    const { addLog, setPendingGraveyardSelection } = context;
    const { myGraveyard, setMyGraveyard, myDeck, setMyDeck, myHand, setMyHand, myRestedSP, setMyRestedSP, myActiveSP, setMyActiveSP } = getPlayerContext(context);

    // 墓地に未来カードが3枚以上あるか確認
    const futureCardsInGraveyard = myGraveyard.filter(c => c.attribute === '未来');
    if (futureCardsInGraveyard.length < 3) {
      addLog(`時の逆流: 墓地の未来属性カードが${futureCardsInGraveyard.length}枚しかありません（3枚必要）`, 'info');
      return false;
    }

    const returnCards = (selectedCards: CardWithId[]) => {
      // 選択したカードをデッキに戻す
      const selectedIds = selectedCards.map(c => c.uniqueId);
      setMyGraveyard(prev => prev.filter(c => !selectedIds.includes(c.uniqueId)));
      setMyDeck(prev => {
        const newDeck = [...prev, ...selectedCards];
        // シャッフル
        for (let i = newDeck.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [newDeck[i], newDeck[j]] = [newDeck[j], newDeck[i]];
        }
        return newDeck;
      });
      addLog(`時の逆流: 墓地から未来属性カード3枚をデッキに戻した！`, 'info');

      // 2枚ドロー
      drawCards(context, 2);
      addLog('時の逆流: 2枚ドロー！', 'info');

      // ライフ1500回復
      healLife(context, 1500, true);
      addLog('時の逆流: ライフを1500回復！', 'heal');

      // レスト状態のSPトークンを全てアクティブにする
      if (myRestedSP > 0) {
        const restedAmount = myRestedSP;
        setMyActiveSP(prev => prev + restedAmount);
        setMyRestedSP(0);
        addLog(`時の逆流: レスト状態のSP${restedAmount}をアクティブ化！`, 'info');
      } else {
        addLog('時の逆流: レスト状態のSPがありません', 'info');
      }
    };

    // 3枚ちょうどの場合は自動選択
    if (futureCardsInGraveyard.length === 3) {
      returnCards(futureCardsInGraveyard);
      return true;
    }

    // 複数ある場合は選択UI
    if (setPendingGraveyardSelection) {
      setPendingGraveyardSelection({
        cards: futureCardsInGraveyard,
        message: 'デッキに戻す未来属性カードを3枚選択',
        count: 3,
        callback: returnCards,
        selectionOwner: context.currentPlayer,
      });
      return true;
    }

    // フォールバック：最初の3枚を選択
    returnCards(futureCardsInGraveyard.slice(0, 3));
    return true;
  },

  /**
   * C0000360: ヴォランティス・テンペスト
   * 基本技: このカードの攻撃力をターン終了時まで800アップし、そのターン中、このカードは相手の効果の対象にならない。
   * 上級技: 自分の手札を2枚デッキに戻し、相手モンスター1体をデッキに戻す（シャッフル後）。
   */
  C0000360: (skillText, context) => {
    const { addLog, monsterIndex, setPendingMonsterTarget, setPendingHandSelection } = context;
    const { myField, setMyField, myHand, setMyHand, myDeck, setMyDeck, opponentField, setOpponentField, setOpponentDeck } = getPlayerContext(context);

    // 基本技: ATK+800、このターン相手の効果対象にならない
    if (context.skillType === 'basic') {
      const thisCard = myField[monsterIndex];
      if (!thisCard) {
        addLog('ヴォランティス・テンペスト: カードが見つかりません', 'info');
        return false;
      }

      setMyField(prev => prev.map((m, idx) => {
        if (idx === monsterIndex && m) {
          const newAtk = (m.currentAttack ?? m.attack ?? 0) + 800;
          return {
            ...m,
            currentAttack: newAtk,
            atkBuffedUntilEndOfTurn: true,
            effectImmunityThisTurn: true,
          };
        }
        return m;
      }));
      addLog(`ヴォランティス・テンペストの基本技: 攻撃力+800！（${(thisCard.currentAttack ?? thisCard.attack ?? 0) + 800}）`, 'info');
      addLog('このターン、このカードは相手の効果の対象にならない！', 'info');
      return true;
    }

    // 上級技: 手札2枚をデッキに戻し、相手モンスター1体をデッキに戻す
    if (context.skillType === 'advanced') {
      // 手札が2枚以上あるか確認
      if (myHand.length < 2) {
        addLog('ヴォランティス・テンペスト: 手札が2枚以上必要です', 'info');
        return false;
      }

      // 相手モンスターがいるか確認
      const opponentMonsters = opponentField
        .map((m, idx) => ({ monster: m, index: idx }))
        .filter(({ monster }) => monster !== null);

      if (opponentMonsters.length === 0) {
        addLog('ヴォランティス・テンペスト: 相手フィールドにモンスターがいません', 'info');
        return false;
      }

      // 手札2枚を選択してデッキに戻す
      setPendingHandSelection({
        message: 'デッキに戻す手札を2枚選択してください',
        count: 2,
        callback: (selectedCards: CardWithId[]) => {
          if (!selectedCards || selectedCards.length !== 2) {
            addLog('ヴォランティス・テンペスト: 手札の選択がキャンセルされました', 'info');
            return;
          }

          // 手札から除去
          const selectedIds = selectedCards.map(c => c.uniqueId);
          setMyHand(prev => prev.filter(c => !selectedIds.includes(c.uniqueId)));

          // デッキに戻してシャッフル
          setMyDeck(prev => {
            const newDeck = [...prev, ...selectedCards];
            for (let i = newDeck.length - 1; i > 0; i--) {
              const j = Math.floor(Math.random() * (i + 1));
              [newDeck[i], newDeck[j]] = [newDeck[j], newDeck[i]];
            }
            return newDeck;
          });
          addLog(`ヴォランティス・テンペストの上級技: 手札2枚をデッキに戻した！`, 'info');
          selectedCards.forEach(c => addLog(`  - ${c.name}`, 'info'));

          // 相手モンスター1体をデッキに戻す
          const currentOpponentMonsters = opponentField
            .map((m, idx) => ({ monster: m, index: idx }))
            .filter(({ monster }) => monster !== null);

          const returnMonster = (targetIndex: number) => {
            const target = opponentField[targetIndex];
            if (!target) return;

            setOpponentField(prev => {
              const newField = [...prev];
              newField[targetIndex] = null;
              return newField;
            });

            setOpponentDeck(prev => {
              const newDeck = [...prev, target];
              for (let i = newDeck.length - 1; i > 0; i--) {
                const j = Math.floor(Math.random() * (i + 1));
                [newDeck[i], newDeck[j]] = [newDeck[j], newDeck[i]];
              }
              return newDeck;
            });
            addLog(`${target.name}をデッキに戻した！`, 'info');
          };

          if (currentOpponentMonsters.length === 1) {
            returnMonster(currentOpponentMonsters[0].index);
          } else if (setPendingMonsterTarget) {
            setPendingMonsterTarget({
              message: 'デッキに戻す相手モンスターを選択',
              targetPlayer: 'opponent',
              callback: returnMonster,
              selectionOwner: context.currentPlayer,
            });
          }
        },
      });
      return true;
    }

    return false;
  },

  /**
   * C0000400: 呪術狩りの技師ヴァルク
   * 【召喚時】デッキから《呪術狩り》カード1枚をサーチ
   * 基本技: SP1個をレスト状態にし、闇属性以外のモンスター1体のHPを800回復
   */
  C0000400: (skillText, context) => {
    const { addLog, setPendingDeckReview, setPendingMonsterTarget } = context;
    const {
      myDeck, setMyDeck, setMyHand, myField, setMyField,
      myActiveSP, setMyActiveSP, setMyRestedSP,
    } = getPlayerContext(context);

    // 召喚時: 《呪術狩り》カードをサーチ
    if (skillText.includes('【召喚時】')) {
      const validCards = myDeck.filter(card =>
        card.name && card.name.includes('呪術狩り')
      );

      if (validCards.length === 0) {
        addLog('呪術狩りの技師ヴァルク【召喚時】: デッキに《呪術狩り》カードがありません', 'info');
        return false;
      }

      if (validCards.length === 1) {
        const card = validCards[0];
        setMyDeck(prev => prev.filter(c => c.uniqueId !== card.uniqueId));
        setMyHand(prev => [...prev, card]);
        addLog(`呪術狩りの技師ヴァルク【召喚時】: ${card.name}を手札に加えた！`, 'info');
        return true;
      }

      setPendingDeckReview({
        message: '手札に加える《呪術狩り》カードを選択',
        cards: validCards,
        count: 1,
        callback: (selectedCards: CardWithId[]) => {
          if (selectedCards && selectedCards.length > 0) {
            const card = selectedCards[0];
            setMyDeck(prev => prev.filter(c => c.uniqueId !== card.uniqueId));
            setMyHand(prev => [...prev, card]);
            addLog(`呪術狩りの技師ヴァルク【召喚時】: ${card.name}を手札に加えた！`, 'info');
          }
        },
        selectionOwner: context.currentPlayer,
      });
      return true;
    }

    // 基本技: SP1個レスト、闇以外のモンスター1体のHP+800
    if (context.skillType === 'basic') {
      if (myActiveSP < 1) {
        addLog('呪術狩りの技師ヴァルク: アクティブSPが1個以上必要です', 'info');
        return false;
      }

      // 自分フィールドの闇属性以外のモンスターを検索
      const validTargets = myField
        .map((m, idx) => ({ monster: m, index: idx }))
        .filter(({ monster }) => monster !== null && monster.attribute !== '闇');

      if (validTargets.length === 0) {
        addLog('呪術狩りの技師ヴァルク: 闘属性以外のモンスターがいません', 'info');
        return false;
      }

      // SPをレスト
      setMyActiveSP(prev => prev - 1);
      setMyRestedSP(prev => prev + 1);

      const healMonster = (targetIndex: number) => {
        setMyField(prev => {
          const newField = [...prev];
          const target = newField[targetIndex];
          if (target) {
            const maxHp = target.maxHp ?? target.hp ?? 0;
            const newHp = Math.min(maxHp, (target.currentHp ?? target.hp ?? 0) + 800);
            newField[targetIndex] = { ...target, currentHp: newHp };
            addLog(`呪術狩りの技師ヴァルクの基本技: ${target.name}のHPを800回復！（HP: ${newHp}）`, 'heal');
          }
          return newField;
        });
      };

      if (validTargets.length === 1) {
        healMonster(validTargets[0].index);
        return true;
      }

      setPendingMonsterTarget({
        message: 'HPを800回復するモンスターを選択',
        targetPlayer: 'self',
        validTargets: validTargets.map(t => t.index),
        callback: (selectedIndex: number) => {
          healMonster(selectedIndex);
        },
        selectionOwner: context.currentPlayer,
      });
      return true;
    }

    return false;
  },

  /**
   * C0000408: 時枯れの骨蜘蛛スケルクロノス
   * 基本技: SP1個をレスト状態にし、相手モンスター1体に800ダメージ
   * 上級技: チャージ1を消費し、相手モンスター1体を次の相手ターン終了時まで除外
   */
  C0000408: (skillText, context) => {
    const { addLog, monsterIndex, setPendingMonsterTarget } = context;
    const {
      myField, setMyField,
      myActiveSP, setMyActiveSP, setMyRestedSP,
      opponentField, setOpponentField, setOpponentGraveyard,
    } = getPlayerContext(context);

    // 基本技: SP1レスト、相手モンスター800ダメージ
    if (context.skillType === 'basic') {
      if (myActiveSP < 1) {
        addLog('時枯れの骨蜘蛛スケルクロノス: アクティブSPが1個以上必要です', 'info');
        return false;
      }

      const validTargets = opponentField
        .map((m, idx) => ({ monster: m, index: idx }))
        .filter(({ monster }) => monster !== null);

      if (validTargets.length === 0) {
        addLog('時枯れの骨蜘蛛スケルクロノス: 相手フィールドにモンスターがいません', 'info');
        return false;
      }

      // SPをレスト
      setMyActiveSP(prev => prev - 1);
      setMyRestedSP(prev => prev + 1);

      const applyDamage = (targetIndex: number) => {
        setOpponentField(prev => {
          const newField = [...prev];
          const target = newField[targetIndex];
          if (target) {
            const newHp = (target.currentHp ?? target.hp ?? 0) - 800;
            if (newHp <= 0) {
              setOpponentGraveyard(g => [...g, target]);
              addLog(`時枯れの骨蜘蛛スケルクロノスの基本技: ${target.name}に800ダメージで破壊！`, 'damage');
              newField[targetIndex] = null;
            } else {
              newField[targetIndex] = { ...target, currentHp: newHp };
              addLog(`時枯れの骨蜘蛛スケルクロノスの基本技: ${target.name}に800ダメージ！（残HP: ${newHp}）`, 'damage');
            }
          }
          return newField;
        });
      };

      if (validTargets.length === 1) {
        applyDamage(validTargets[0].index);
        return true;
      }

      setPendingMonsterTarget({
        message: '800ダメージを与える相手モンスターを選択',
        targetPlayer: 'opponent',
        callback: (selectedIndex: number) => {
          applyDamage(selectedIndex);
        },
        selectionOwner: context.currentPlayer,
      });
      return true;
    }

    // 上級技: チャージ1消費、相手モンスター1体を次の相手ターン終了時まで除外
    if (context.skillType === 'advanced') {
      const thisCard = myField[monsterIndex];
      if (!thisCard) {
        addLog('時枯れの骨蜘蛛スケルクロノス: カードが見つかりません', 'info');
        return false;
      }

      // チャージ確認
      if (!thisCard.charges || thisCard.charges.length === 0) {
        addLog('時枯れの骨蜘蛛スケルクロノス: チャージが必要です', 'info');
        return false;
      }

      const validTargets = opponentField
        .map((m, idx) => ({ monster: m, index: idx }))
        .filter(({ monster }) => monster !== null);

      if (validTargets.length === 0) {
        addLog('時枯れの骨蜘蛛スケルクロノス: 相手フィールドにモンスターがいません', 'info');
        return false;
      }

      // チャージ消費
      setMyField(prev => prev.map((m, idx) => {
        if (idx === monsterIndex && m) {
          const newCharges = [...(m.charges ?? [])];
          newCharges.pop();
          return { ...m, charges: newCharges, chargeCount: Math.max(0, (m.chargeCount ?? 0) - 1) };
        }
        return m;
      }));
      addLog('時枯れの骨蜘蛛スケルクロノスの上級技: チャージ1を消費！', 'info');

      const banishMonster = (targetIndex: number) => {
        const target = opponentField[targetIndex];
        if (!target) return;

        // フィールドから除去（除外ゾーンに送る = 一時除外フラグ設定）
        setOpponentField(prev => {
          const newField = [...prev];
          newField[targetIndex] = null;
          return newField;
        });

        // 除外情報をgameStateに保存（magic-spirit.jsxで処理）
        // ここではログだけ出力
        addLog(`時枯れの骨蜘蛛スケルクロノスの上級技: ${target.name}を次の相手ターン終了時まで除外！`, 'info');

        // 除外されたモンスター情報を保存（簡易実装：墓地に特殊フラグ付きで送る）
        // 本来は除外ゾーンを実装すべきだが、現状は破壊扱い
        setOpponentGraveyard(prev => [...prev, { ...target, temporarilyBanished: true }]);
      };

      if (validTargets.length === 1) {
        banishMonster(validTargets[0].index);
        return true;
      }

      setPendingMonsterTarget({
        message: '除外する相手モンスターを選択',
        targetPlayer: 'opponent',
        callback: (selectedIndex: number) => {
          banishMonster(selectedIndex);
        },
        selectionOwner: context.currentPlayer,
      });
      return true;
    }

    return false;
  },

  /**
   * C0000431: 灰塵の刻印
   * 【刹那詠唱】相手モンスター1体を攻撃不能にする。このターン中、そのモンスターが受けるダメージを300アップ。
   */
  C0000431: (skillText, context) => {
    const { addLog, setPendingMonsterTarget } = context;
    const { opponentField, setOpponentField } = getPlayerContext(context);

    // 相手モンスターを確認
    const opponentMonsters = opponentField
      .map((m, idx) => ({ monster: m, index: idx }))
      .filter(({ monster }) => monster !== null);

    if (opponentMonsters.length === 0) {
      addLog('灰塵の刻印: 相手フィールドにモンスターがいません', 'info');
      return false;
    }

    // 効果を適用する関数
    const applyEffect = (targetIndex: number) => {
      setOpponentField((prev) => {
        const newField = [...prev];
        const target = newField[targetIndex];

        if (target) {
          newField[targetIndex] = {
            ...target,
            canAttack: false, // 攻撃不能
            ashMarkDamageBoost: 300, // 被ダメージ+300
            ashMarkActive: true, // このターンのみ有効
          };
          addLog(`灰塵の刻印: ${target.name}を攻撃不能にし、被ダメージを300アップ！`, 'damage');
        }
        return newField;
      });
    };

    // 1体のみの場合は自動選択
    if (opponentMonsters.length === 1) {
      applyEffect(opponentMonsters[0].index);
      return true;
    }

    // 複数いる場合は選択UI
    if (setPendingMonsterTarget) {
      setPendingMonsterTarget({
        message: '対象の相手モンスターを選択',
        targetPlayer: 'opponent',
        callback: (selectedIndex: number) => {
          applyEffect(selectedIndex);
        },
        selectionOwner: context.currentPlayer,
      });
      return true;
    }

    applyEffect(opponentMonsters[0].index);
    return true;
  },

  /**
   * C0000451: プロフェシードラゴン
   * 基本技: 自分のデッキの上のカードを確認し、そのコスト×200のダメージを相手プレイヤーに与える。
   */
  C0000451: (skillText, context) => {
    const { addLog, setPendingDeckReview } = context;
    const { myDeck, setOpponentLife } = getPlayerContext(context);

    if (context.skillType === 'basic') {
      // デッキが空の場合
      if (!myDeck || myDeck.length === 0) {
        addLog('プロフェシードラゴン: デッキにカードがありません', 'info');
        return false;
      }

      // デッキトップ1枚を確認
      const topCard = myDeck[0];
      const cardCost = topCard.cost || 0;
      const damage = cardCost * 200;

      addLog(`プロフェシードラゴンの基本技発動！デッキトップを確認: ${topCard.name}（コスト${cardCost}）`, 'info');

      // ダメージを与える
      if (damage > 0) {
        setOpponentLife(prev => Math.max(0, prev - damage));
        addLog(`プロフェシードラゴン: 相手に${damage}ダメージ！（コスト${cardCost} × 200）`, 'damage');
      } else {
        addLog('プロフェシードラゴン: コスト0のため、ダメージなし', 'info');
      }

      // デッキ確認UIを表示（任意）
      if (setPendingDeckReview) {
        setPendingDeckReview({
          cards: [topCard],
          title: 'プロフェシードラゴン',
          message: `デッキトップ: ${topCard.name}（コスト${cardCost}）→ ${damage}ダメージ`,
          allowReorder: false,
          onConfirm: () => {
            // 確認のみ、デッキは変更しない
          },
          selectionOwner: context.currentPlayer,
        });
      }

      return true;
    }

    return false;
  },

  /**
   * C0000561: カーテン職人デンガク
   * 基本技: 自分の手札に遮光カーテンを生成する。
   */
  C0000561: (skillText, context) => {
    const { addLog } = context;
    const { setMyHand } = getPlayerContext(context);

    if (context.skillType === 'basic') {
      // 遮光カーテンカードを生成
      const shaderCurtain = {
        id: 'C0000560',
        uniqueId: `C0000560_generated_${Date.now()}_${Math.random()}`,
        name: '遮光カーテン',
        attribute: '闇',
        cost: 1,
        type: 'magic',
        effect: '相手フィールドの光属性モンスター1体を手札に戻す。',
      };

      setMyHand(prev => [...prev, shaderCurtain as any]);
      addLog('カーテン職人デンガクの基本技: 「遮光カーテン」を手札に加えた！', 'info');
      return true;
    }

    return false;
  },

  /**
   * C0000419: 毒蛾モルフィグナ
   * 基本技: SPトークン1個をレスト状態にし、次のターン終了時までこのカードが受けるダメージを300軽減。
   * ※召喚時効果（400ダメージ）はfutureCards.tsのトリガーで実装済み
   */
  C0000419: (skillText, context) => {
    const { addLog, monsterIndex } = context;
    const { myField, setMyField, myActiveSP, setMyActiveSP, setMyRestedSP } = getPlayerContext(context);

    if (context.skillType === 'basic') {
      // SP確認
      if (myActiveSP < 1) {
        addLog('アクティブSPが1個以上必要です', 'info');
        return false;
      }

      // SP1個をレスト
      setMyActiveSP((prev: number) => prev - 1);
      setMyRestedSP((prev: number) => prev + 1);

      // 自分自身にダメージシールドを付与
      const targetMonster = myField[monsterIndex!];
      if (targetMonster) {
        statusEffectEngine.applyStatus(targetMonster, STATUS_EFFECT_TYPES.DAMAGE_SHIELD, {
          source: 'C0000419',
          sourceName: '毒蛾モルフィグナ',
          value: 300,
          expiresAfterEndPhases: 2, // 次のターン終了時まで
        });
        setMyField((prev: (MonsterInstance | null)[]) => prev.map((m, i) =>
          i === monsterIndex ? { ...targetMonster } : m
        ));
      }

      addLog('毒蛾モルフィグナの基本技: SP1個をレスト、次のターン終了時まで受けるダメージを300軽減！', 'info');
      return true;
    }

    return false;
  },
};
